import { Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';
import { ApiErrorService } from 'src/app/core/services/api-error.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { EventService } from 'src/app/core/services/event.service';
import { MenuService } from 'src/app/core/services/menu.service';
import { User } from 'src/app/core/models/auth.models';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { PropertiesService } from 'src/app/pages/properties/services/properties.services';
import { ChatBannerIssue } from './components/chat-connection-banner/chat-connection-banner.component';
import { ChatImageOpen } from './components/chat-message/chat-message.component';
import { ChatPropertyScope } from './components/chat-property-picker/chat-property-picker.component';
import {
  buildIcs, buildQuickPicks, buildReminderPicks, ChatQuickPick, ChatTemplateVars, combineDateTime, formatPrice, formatSystemText, formatTime, formatWhen,
  messageKey, nextRoundHour, normalizeSearch, quickPickFromIso, toDateInput, toTimeInput, withinWindow
} from './chat.format';
import { ToastrService } from 'ngx-toastr';
import { CHAT_STRINGS_PT, ChatStrings } from './chat.strings';
import {
  ChatAssignResult, ChatComposerSubmit, ChatConversation, ChatMessage, ChatMessageFile, ChatMessageKind, ChatParticipant, ChatPresenceMap, ChatPresenceStatus, ChatPropertyPick, ChatQuickReply, ChatUser, ChatVisitAction, ChatVisitRow, ChatCardAction, ChatDealStage, ChatDealStages, ChatMention, ChatNextAction, ChatNextStep, ChatQualificationAnswers, ChatComposerAction, ChatMessageReply,
  ChatComposerSchedule, ChatReactionGroup, ChatScheduledMessage,
  NON_CONVERSATIONAL_KINDS, isClientRow, isStaffRow
} from './chat.model';
import { ChatInvite, ChatPendingInvite, qualificationLists, ChatColleague, ChatColleagueScope, ChatColleaguesPage } from './chat.model';
import { CHAT_NO_CAPABILITIES, ChatCapabilities, ChatConnectionIssue, ChatService } from './services/chat.service';

/** localStorage: os passos da faixa "próximo passo" que o consultor dispensou, por conversa. */
const NEXT_STEP_DISMISS_KEY = 'chat-next-step:';

/** O que abre o modal de marcar já preenchido (cartão de imóvel, pedido respondido, reagendar). */
interface VisitPrefill {
  /** 2026-10-27: "Agendar agora" ou "Perguntar disponibilidade" no mesmo modal. */
  mode?: 'now' | 'ask';
  startAt?: string;
  requestMessageId?: string;
  durationMinutes?: number;
  property?: ChatPropertyPick | null;
  reschedule?: { visitId: number };
}

/** Limites dos anexos de POST /chatv2/chat/messages/files (contrato, secção 5). */
const CHAT_MAX_FILES = 20;
const CHAT_MAX_FILE_BYTES = 5 * 1024 * 1024;
const CHAT_MAX_TOTAL_MB = 25;
const CHAT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
/**
 * Uma mensagem enviada que o servidor não ecoou ao fim deste tempo, com o chat
 * LIGADO, aparece como "Não foi enviada" com "Tentar de novo". Com o chat
 * desligado a espera continua: o socket.io guarda o emit e envia-o ao religar
 * ("as mensagens seguem quando voltar").
 */
const SEND_TIMEOUT_MS = 30000;
/** "a escrever…" apaga-se sem novo `chat_typing` ao fim deste tempo (contrato 6.3). */
const TYPING_EXPIRE_MS = 5000;
/** localStorage: o painel "Detalhes" aberto/fechado, lembrado entre visitas. */
const DETAILS_PREF_KEY = 'imodigi-chat-details';

/** As imagens de uma mensagem abertas no lightbox e a que está no ecrã. */
interface ChatLightbox {
  files: ChatMessageFile[];
  index: number;
}

/** O que a topbar entrega para abrir uma conversa (MenuService / evento 'menu-chat'). */
interface ChatMenuTarget {
  menu?: string;
  chat?: string;
  userId?: string;
}

/**
 * A página de chat do CRM: o ÚNICO componente que fala com o ChatService, o
 * EventService, o MenuService e a autenticação. Tudo o que está no ecrã são
 * componentes de apresentação alimentados por inputs; esta classe é dona dos
 * dados e aplica cada alteração de forma imutável (arrays / objetos novos),
 * que é o que deixa os filhos correr em OnPush.
 *
 * Copiado uma vez do contentor da biblioteca da PWA e adaptado ao CRM:
 *  - sem tokens de injeção (environment, AuthService, EventService, MenuService);
 *  - dados de `/chat/users` do back-end (chaves `image`, `dateTime`,
 *    `supervised` / `ownerUserId` / `ownerName`, `propertyLink` e, na versão 2,
 *    `assignedUserAppId` / `assignedName` / `participants`);
 *  - dois painéis lado a lado dentro do layout do CRM, sem disposição móvel;
 *  - separadores "Todas / Não lidas / Equipa / Por atribuir", faixa de
 *    atribuição e painel "Atribuir a" (`GET /chat/queue`, `POST /chat/:chat/assign`,
 *    `chat_assigned`);
 *  - "Convidar colega" (modal ng-bootstrap + ng-select -> `invite_to_chat`),
 *    "Enviar imóvel" (modal com a pesquisa de imóveis do CRM -> `send_property`),
 *    presença própria (`set_presence`), "a escrever…" (`typing` / `chat_typing`)
 *    e respostas rápidas (`GET /chat/quick-replies`);
 *  - abre a conversa pedida pela topbar (MenuService + 'menu-chat') e avisa a
 *    topbar com 'update-notifications-messages', como a página antiga.
 */
@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('inviteModal') inviteModal!: TemplateRef<any>;
  @ViewChild('propertyModal') propertyModal!: TemplateRef<any>;
  @ViewChild('lightboxModal') lightboxModal!: TemplateRef<any>;

  strings: ChatStrings = CHAT_STRINGS_PT;
  users: ChatUser[] = [];
  /** `GET /chat/queue` (só com canAssign): conversas sem responsável, mais antigas primeiro. */
  queue: ChatUser[] = [];
  /** A fila filtrada pela pesquisa da lista (como `users` é `allUsers` filtrado). */
  queueVisible: ChatUser[] = [];
  /** Não lidas em TODA a lista (clientes / colegas): o rodapé não muda enquanto se pesquisa. */
  unreadTotals: { clients: number; staff: number } = { clients: 0, staff: 0 };
  activeConversation: ChatConversation | null = null;
  propertyRoute: any[] | null = null;
  propertyQueryParams: { [key: string]: any } | null = null;
  capabilities: ChatCapabilities = CHAT_NO_CAPABILITIES;
  private identityRefreshAsked = false;
  connectionIssue: ChatConnectionIssue | null = null;
  chatDisconnected = false;
  loadingList = false;
  loadingMessages = false;
  assignOpen = false;
  assignBusy = false;
  onlineIds: string[] = [];
  presence: ChatPresenceMap = {};
  /** O estado que o próprio escolheu (ChatService.myPresence$). */
  myPresence: 'online' | 'away' = 'online';
  /** O outro lado está a escrever na conversa aberta (`chat_typing`, expira em 5 s). */
  typingActive = false;
  /** O que o outro lado está a fazer: a escrever, ou a gravar uma mensagem de voz. */
  typingMode: 'text' | 'voice' = 'text';
  /** Nomes dos outros membros do staff na conversa aberta (convidados), para o cabeçalho. */
  participantNames: string[] = [];
  /** Respostas rápidas ativas (próprias + gerais), para o compositor. */
  quickReplies: ChatQuickReply[] = [];
  userLogged: User;
  readonly settingsLink = ['/chat', 'settings'];

  /* apresentação: painel de detalhes, pesquisa na conversa, lightbox */
  /** Coluna da direita "Detalhes" (lembrada em localStorage; aberta por omissão em ecrãs largos). */
  detailsOpen = readDetailsPref();
  threadSearchOpen = false;
  threadSearch = '';
  threadHitIndex = -1;
  /** Chave (`messageKey`) do resultado da pesquisa que o fio deve mostrar. */
  threadFocusKey: string | null = null;
  lightbox: ChatLightbox | null = null;
  private hitsCache: { messages: ChatMessage[] | null; term: string; keys: string[] } = { messages: null, term: '', keys: [] };

  /* modal "Convidar consultor" */
  inviteUserId: string | null = null;
  /** Opções do modal "Convidar", calculadas ao abrir (ver computeInviteCandidates). */
  inviteOptions: ChatUser[] = [];
  /* visitas (7.3): modal "Agendar visita" e "Pedir avaliação" */
  /** Dentro do balão flutuante do layout: uma coluna (lista ↔ conversa) numa altura fixa. */
  @Input() widget = false;
  /**
   * false enquanto o balão está escondido: o que chega à conversa aberta não se
   * marca como lido (ninguém o vê) e conta no badge; ao voltar a true lê-se.
   */
  @Input() active = true;
  @ViewChild('visitModal') visitModal!: TemplateRef<any>;
  @ViewChild('visitFormModal') visitFormModal!: TemplateRef<any>;
  visitDate = '';
  visitTime = '';
  visitDuration = 60;
  visitSendEmail = true;
  /** As entradas da agenda que se sobrepõem ao horário escolhido (aviso, nunca bloqueia). */
  visitConflicts: { name: string; from: string; to: string }[] = [];
  visitBusy = false;
  /** O pedido de disponibilidade que esta marcação fecha (quando vem do cartão). */
  visitRequestMessageId: string | null = null;
  /** O imóvel da visita / dos horários / da proposta, mostrado dentro do modal (o da conversa por omissão). */
  visitProperty: ChatPropertyPick | null = null;
  /** Atalhos de data do modal de marcar ("Hoje 15h", "Amanhã 10h", …). */
  visitQuickPicks: ChatQuickPick[] = [];
  /** Atalhos do modal de lembrete. */
  reminderQuickPicks: ChatQuickPick[] = [];
  /** O cartão de visita cuja ação de um clique está a correr (o botão mostra um spinner). */
  visitActionBusy: string | null = null;
  /** A que modal se volta depois de trocar o imóvel no seletor. */
  private propertyReturn: 'visit' | 'slots' | 'proposal' | null = null;
  /** Secção 11: o modal de marcar está a REAGENDAR esta visita (em vez de criar uma). */
  visitReschedule: { visitId: number } | null = null;
  private agendaPicksSeq = 0;
  /* fase 3: próximo passo e imóveis que encaixam na qualificação */
  /** Os imóveis que encaixam na última qualificação respondida (até 3), com os filtros que os encontraram. */
  matchingProperties: ChatPropertyPick[] = [];
  private matchingFilters: any = null;
  private matchingSeq = 0;
  /** Ids a pré-selecionar no seletor quando os resultados chegarem ("Ver e enviar"). */
  private propertyPreselect: string[] | null = null;
  /* secção 12: as mensagens agendadas pelo próprio na conversa aberta */
  scheduled: ChatScheduledMessage[] = [];
  private scheduleToastWhen: string | null = null;
  /** Passos dispensados nesta conversa (`localStorage`), por chave. */
  private dismissedSteps: { [chat: string]: string[] } = {};
  private nextStepCache: { messages: ChatMessage[] | null; visits: ChatVisitRow[] | null; matching: number; step: ChatNextStep | null } =
    { messages: null, visits: null, matching: -1, step: null };
  /** "Responder eu" na qualificação: abre as respostas assim que o cartão ecoar. */
  private openQualAnswersOnEcho = false;
  /** Secção 9: a mensagem em edição no compositor (como no WhatsApp). */
  editingMessage: ChatMessage | null = null;
  /** Secção 10: a mensagem a que o compositor está a responder. */
  replyingTo: ChatMessage | null = null;
  private jumpTimer: any = null;
  /** Edições já mostradas e à espera do eco do servidor: o texto anterior, para repor se recusar. */
  private pendingEdits: { [messageId: string]: { message: string; editedAt: any } } = {};
  visitSlots: { date: string; time: string }[] = [];
  visitNote = '';
  /** "Agendar visita" (2026-10-27): agendar já, ou perguntar a disponibilidade ao cliente. */
  visitMode: 'now' | 'ask' = 'now';
  visitList: ChatVisitRow[] = [];
  visitListLoading = false;
  visitFormBusy: number | null = null;
  readonly visitDurations = [30, 45, 60, 90, 120];
  /* fase 3: qualificação, proposta, negócio, lembretes, transferência */
  @ViewChild('qualificationModal') qualificationModal!: TemplateRef<any>;
  @ViewChild('transferModal') transferModal!: TemplateRef<any>;
  @ViewChild('reminderModal') reminderModal!: TemplateRef<any>;
  readonly qualFieldKeys = ['propertyType', 'businessType', 'budget', 'bedrooms', 'notes'];
  qualFields: { [key: string]: boolean } = {};
  /** Quartos como chips (5 = "T5+") e tetos de orçamento comuns, para responder em segundos. */
  readonly qualBedroomChips = [0, 1, 2, 3, 4, 5];
  readonly qualBudgetChips = [150000, 250000, 400000, 600000, 1000000];
  qualEditing = false;
  qualMessage: ChatMessage | null = null;
  qualOptions: { propertyTypes: any[]; businessTypes: any[] } = { propertyTypes: [], businessTypes: [] };
  qualAnswers: ChatQualificationAnswers = {};
  openNewProposal = false;
  proposalDeal: any = null;
  /** A conversa para onde a proposta do modal partilhado vai (fixada ao abrir, não ao fechar). */
  private proposalChatId: string | null = null;
  /**
   * Toasts de sucesso de ações pelo socket (sem ack): só se mostram quando o
   * servidor ecoa o resultado. Chave = kind da mensagem, `system:<code>` ou
   * `update:<messageId>`; valor = o texto.
   */
  private awaitingToasts: { [key: string]: string } = {};
  proposalLeadId: number | null = null;
  dealStages: ChatDealStages | null = null;
  nextAction: ChatNextAction | null = null;
  stageBusy = false;
  transferOptions: ChatUser[] = [];
  transferUserId: string | null = null;
  transferNote = '';
  reminderDate = '';
  reminderTime = '';
  reminderNote = '';
  /** Filtros do modal "Enviar imóvel" quando vem da qualificação ("Procurar imóveis"). */
  propertyFilters: any = null;
  /** Visitas da conversa aberta (a ficha de visita existe logo que a visita fica agendada). */
  chatVisits: ChatVisitRow[] = [];
  /** O seletor de imóveis: enviar cartões, ou escolher UM imóvel para a visita / horários / proposta. */
  propertyPickMode: 'send' | 'confirm' = 'send';
  /* nova conversa entre colegas */
  @ViewChild('newConversationModal') newConversationModal!: TemplateRef<any>;
  colleagueOptions: ChatUser[] = [];
  newConversationId: string | null = null;
  /* diretório de colegas (2026-10-28): nova conversa, convidar, transferir, mencionar */
  colleagues: ChatColleaguesPage = { scope: 'store', master: false, stores: [], users: [] };
  colleaguesLoading = false;
  colleagueScope: ChatColleagueScope = 'store';
  colleagueStoreId: number | null = null;
  colleagueSearch = '';
  /** `userAppId`s escondidos no seletor aberto (já na conversa, convite pendente, o responsável). */
  colleagueExclude: string[] = [];
  private colleaguesSeq = 0;
  /** A rede inteira, lida uma vez, para as menções. */
  private colleagueMentionPool: ChatColleague[] | null = null;
  /** Opções do ng-select da duração ("45 min"), como o resto dos selects do CRM. */
  readonly visitDurationOptions = this.visitDurations.map(d => ({ value: d, label: d + ' ' + CHAT_STRINGS_PT.minutesShort }));
  private availabilityTimer: any = null;
  /* modal "Enviar imóvel" */
  /** "Ver e enviar": os ids que o seletor abre já marcados. */
  propertyPreselectIds: string[] = [];
  propertySelected: ChatPropertyPick[] = [];
  /** O âmbito com que o seletor abre: "Ver e enviar" abre no mais largo, onde os imóveis foram contados (2026-10-28). */
  propertyPickerScope: ChatPropertyScope = 'store';

  private allUsers: ChatUser[] = [];
  /** Conversa pedida (`/chat?chat=<id>` ou aviso do balão) que ainda não está na lista: abre-se quando chegar. */
  private pendingChatParam: string | null = null;
  /** Contrato 6.3, v8: convites para conversas que esperam pela minha resposta (topo da lista). */
  invites: ChatInvite[] = [];
  /** O convite com uma resposta a caminho. */
  inviteBusy: string | null = null;
  /** Convidados da conversa aberta que ainda não responderam (painel de detalhes). */
  pendingInvites: ChatPendingInvite[] = [];
  private searchTerm = '';
  private pendingFiles: { [idTemp: string]: File[] } = {};
  private sendTimers: { [clientMessageId: string]: any } = {};
  private clientSeq = 0;
  private subscriptions = new Subscription();
  private usersSubscription?: Subscription;
  private queueSubscription?: Subscription;
  private queueLoaded = false;
  private quickLoaded = false;
  private typingTimer: any = null;
  /** O que `presence$` dizia do próprio na última emissão (para só reagir a mudanças). */
  private mySeenPresence: ChatPresenceStatus | undefined;
  private modalRef: NgbModalRef | null = null;
  private readonly pathFile: string = environment.dirChatFiles;
  private readonly pathProfileImage = `${environment.apiUrl}/${environment.dirProfileImage}/`;
  private readonly pathPropertyImage = `${environment.apiUrl}${environment.dirPropertiesImages}/`;

  constructor(
    private chatService: ChatService,
    private authService: AuthService,
    private eventService: EventService,
    private menuService: MenuService,
    private modalService: NgbModal,
    private propertiesService: PropertiesService,
    private permissionsService: PermissionsService,
    private apiError: ApiErrorService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.userLogged = this.authService.currentUserValue || {};
  }

  /* ---------------------------------------------------------------------
   * ciclo de vida
   * ------------------------------------------------------------------ */

  ngOnInit(): void {
    // No balão (400px) o painel de detalhes tapava a conversa: começa fechado, e a
    // preferência da página completa não é tocada em modo widget.
    if (this.widget) {
      this.detailsOpen = false;
    }
    // Tudo entra em `subscriptions`: o ChatService e o EventService são
    // singletons, e um handler esquecido sobrevive à página.
    this.subscriptions.add(this.chatService.chatDisconnected$.subscribe(value => this.chatDisconnected = value));
    this.subscriptions.add(this.chatService.connectionIssue$.subscribe(issue => this.connectionIssue = issue));
    this.subscriptions.add(this.chatService.capabilities$.subscribe(caps => {
      this.capabilities = caps || CHAT_NO_CAPABILITIES;
      // A fila só existe para quem atribui; as respostas rápidas para quem tem chat.
      if (this.capabilities.canAssign && !this.queueLoaded) {
        this.loadQueue();
      }
      if (this.capabilities.chatIdentity && !this.quickLoaded) {
        this.loadQuickReplies();
      }
    }));
    this.subscriptions.add(this.chatService.myPresence$.subscribe(status => this.myPresence = status));
    // O modal de propostas partilhado avisa por EventService quando fecha (7.3).
    this.subscriptions.add(this.eventService.subscribe('new-proposal-closed', () => this.onProposalModalClosed()));
    this.subscriptions.add(this.chatService.presence$.subscribe(map => {
      this.presence = map || {};
      this.syncMyPresence();
    }));
    this.subscriptions.add(this.chatService.usersOnline$.subscribe(list => {
      const rows = Array.isArray(list) ? list : [];
      this.onlineIds = rows.map((row: any) => row && String(row.id)).filter((id: string) => !!id);
    }));

    // A topbar pede uma conversa: por 'menu-chat' quando já estamos em /chat,
    // pelo MenuService quando navega para cá (consumido depois de carregar a lista).
    this.subscriptions.add(this.eventService.subscribe('menu-chat', data => this.openFromMenu(data)));
    this.subscriptions.add(this.eventService.subscribe('refresh-chat', () => this.initChat()));
    // `/chat?chat=<id>`: abrir uma conversa por link. Só a página; o balão não tem rota.
    if (!this.widget) {
      this.subscriptions.add(this.route.queryParamMap.subscribe(params => {
        const chat = params.get('chat');
        if (chat) {
          this.pendingChatParam = chat;
          if (!this.loadingList) {
            this.openPendingChat();
          }
        }
      }));
    }

    this.listenSocket();
    this.initChat();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active'] && !changes['active'].firstChange && this.active) {
      this.readActiveIfUnread();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.usersSubscription) {
      this.usersSubscription.unsubscribe();
    }
    if (this.queueSubscription) {
      this.queueSubscription.unsubscribe();
    }
    Object.keys(this.sendTimers).forEach(key => clearTimeout(this.sendTimers[key]));
    this.sendTimers = {};
    this.clearTypingTimer();
    this.closeModal();
  }

  /* ---------------------------------------------------------------------
   * ajudas da vista
   * ------------------------------------------------------------------ */

  /**
   * `UserApp.id` do utilizador com sessão: o login do back-end devolve
   * `userApp` (a conta de chat na entidade). É o id que o chat usa em tudo
   * (contrato, regra 3) e o `userAppId` de "Assumir eu".
   */
  get meId(): string | undefined {
    // A identidade que o servidor resolveu para este socket manda (chat_capabilities.userAppId,
    // contrato 6.1): o `userApp` do login pode ser outra conta, ou faltar.
    if (this.capabilities && this.capabilities.userAppId) {
      return String(this.capabilities.userAppId);
    }
    return this.userLogged && this.userLogged.userApp && this.userLogged.userApp.id
      ? String(this.userLogged.userApp.id)
      : undefined;
  }

  /** contrato 6.1: autenticado, sem conta de chat. Um estado, nunca "a ligar". */
  get noChatIdentity(): boolean {
    return this.connectionIssue === 'no_chat_identity';
  }

  get bannerIssue(): ChatBannerIssue | null {
    if (this.connectionIssue === 'no_chat_identity') {
      return null;
    }
    if (this.connectionIssue) {
      return this.connectionIssue;
    }
    return this.chatDisconnected ? 'offline' : null;
  }

  get activeKey(): string | null {
    return this.activeConversation ? this.keyOf(this.activeConversation) : null;
  }

  get activePresence(): ChatPresenceStatus | null {
    const id = this.activeConversation && this.activeConversation.userId;
    if (!id) {
      return null;
    }
    return this.presence[id] || (this.onlineIds.indexOf(id) > -1 ? 'online' : null);
  }

  /** Notas só numa conversa COM UM CLIENTE: entre colegas a conversa já é privada. */
  get canWriteNotesHere(): boolean {
    return !!this.capabilities.canWriteNotes && this.activeIsClient;
  }

  /** Conversa de CLIENTE aberta, com id e não supervisionada: onde as ações de negócio existem. */
  get activeIsClient(): boolean {
    const conv = this.activeConversation;
    return !!conv && !!conv.chat && !conv.supervised && isClientRow(conv);
  }

  get canQualifyHere(): boolean {
    return this.activeIsClient && !!this.capabilities.canQualify;
  }

  /**
   * A faixa de atribuição, só numa conversa de cliente com id:
   *  - quem atribui (diretor / operador / master): sempre que não há responsável,
   *    e numa conversa supervisionada de outro consultor com o cliente à espera;
   *  - um consultor: só quando não há responsável E o cliente está à espera
   *    ("Assumir eu"; o servidor deixa qualquer participante assumir).
   */
  get showAssignBanner(): boolean {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !isClientRow(conv)) {
      return false;
    }
    const noOwner = !conv.assignedUserAppId;
    if (this.capabilities.canAssign) {
      return noOwner || (conv.assignedUserAppId !== this.meId && !!conv.supervised && !!this.unansweredSince);
    }
    return noOwner && !conv.supervised && !!this.meId && !!this.unansweredSince;
  }

  /** "Assumir eu": precisa do próprio `userAppId`; sem responsável qualquer staff, com responsável só quem atribui. */
  get canTakeOver(): boolean {
    const conv = this.activeConversation;
    if (!conv || !this.meId || conv.assignedUserAppId === this.meId) {
      return false;
    }
    return !conv.assignedUserAppId || !!this.capabilities.canAssign;
  }

  /** Hora da última mensagem do cliente, se a equipa ainda não respondeu; senão null. */
  get unansweredSince(): any {
    const conv = this.activeConversation;
    if (!conv || conv.messages.length === 0) {
      return null;
    }
    // Sobem-se as linhas que não são resposta da equipa nem pergunta do cliente
    // (a mesma lista do back-end, NON_CONVERSATIONAL_KINDS).
    for (let i = conv.messages.length - 1; i >= 0; i--) {
      const m = conv.messages[i];
      if (NON_CONVERSATIONAL_KINDS.indexOf(m.kind || 'text') > -1) {
        continue;
      }
      return m.userSender && m.userSender.id === conv.userId ? m.time : null;
    }
    return null;
  }

  /** Colegas com chat (qualquer papel de staff, não supervisionados): as linhas do painel "Atribuir a". */
  get consultants(): ChatUser[] {
    return this.dedupeByUser(this.allUsers.filter(u => isStaffRow(u) && !u.supervised && !!u.userId && u.userId !== this.meId));
  }

  /**
   * Colegas (staff da entidade com chat) que ainda não estão na conversa
   * aberta: o ng-select de "Convidar". Calcula-se UMA vez ao abrir o modal
   * (`inviteOptions`): como getter, devolvia um array novo a cada ciclo de
   * deteção e o ng-select reconstruía as opções sem deixar selecionar.
   */
  private computeInviteCandidates(): ChatUser[] {
    const conv = this.activeConversation;
    const present: { [id: string]: boolean } = {};
    ((conv && conv.participants) || []).forEach(p => present[String(p.id)] = true);
    // Contrato 6.3, v8: quem ja tem um convite por aceitar tambem nao aparece.
    this.pendingInvites.forEach(p => present[String(p.userAppId)] = true);
    return this.dedupeByUser(this.allUsers.filter(u => !!u.userId && isStaffRow(u) && !u.supervised
      && u.userId !== this.meId && (!conv || u.userId !== conv.userId) && !present[u.userId]));
  }

  /** Responsável da conversa aberta, quando é outra pessoa (o cabeçalho não anuncia "responsável: eu"). */
  get activeAssignedName(): string | undefined {
    const conv = this.activeConversation;
    if (!conv || conv.supervised || !conv.assignedUserAppId || conv.assignedUserAppId === this.meId) {
      return undefined;
    }
    return conv.assignedName || this.nameOf(conv.assignedUserAppId);
  }

  get supervisedReadOnlyText(): string {
    const conv = this.activeConversation;
    return this.strings.supervisedReadOnly.replace('{name}', (conv && conv.ownerName) || this.strings.roleAgent);
  }

  get propertySelectedText(): string {
    const n = this.propertySelected.length;
    if (n === 0) {
      return this.strings.propertyNoneSelected;
    }
    return n === 1 ? this.strings.propertySelectedOne : this.strings.propertySelected.replace('{n}', String(n));
  }

  get sendPropertiesLabel(): string {
    const n = this.propertySelected.length;
    return n <= 1 ? this.strings.sendPropertyOne : this.strings.sendProperties.replace('{n}', String(n));
  }

  get sendPropertyTitle(): string {
    const conv = this.activeConversation;
    return this.strings.sendPropertyTitle.replace('{name}', (conv && conv.name) || '');
  }

  /** A coluna da direita existe quando há conversa e ou o painel "Atribuir a" ou os detalhes estão abertos. */
  get asideOpen(): boolean {
    return !!this.activeConversation && ((this.assignOpen && !!this.capabilities.canAssign) || this.detailsOpen);
  }

  /** Base das fotos de perfil (`/users/files/`), para o painel de detalhes resolver `participants[].profile`. */
  get profileImageBase(): string {
    return this.pathProfileImage;
  }

  /**
   * Valores das {{variáveis}} das respostas rápidas para a conversa aberta:
   * nome do cliente, imóvel (título ou referência), consultor (eu) e agência.
   * O preço vem do último cartão de imóvel enviado nesta conversa, se houver.
   */
  get templateVars(): ChatTemplateVars | null {
    const conv = this.activeConversation;
    if (!conv) {
      return null;
    }
    let preco: string | undefined;
    for (let i = conv.messages.length - 1; i >= 0; i--) {
      const m = conv.messages[i];
      if (m.kind === 'property' && m.property && m.property.price) {
        preco = formatPrice(m.property.price, m.property.businessType === 'rent');
        break;
      }
    }
    return {
      nome: isClientRow(conv) ? conv.name : undefined,
      imovel: conv.propertyTitle || conv.propertyReference || undefined,
      preco,
      consultor: this.meName || undefined,
      agencia: (this.userLogged && (this.userLogged as any).entityName) || undefined
    };
  }

  /** Nome e foto do utilizador ligado, para o avatar do topo da lista. */
  get meName(): string {
    return (this.userLogged && this.userLogged.name) || '';
  }

  get mePhoto(): string | undefined {
    const u: any = this.userLogged || {};
    const file = u.userFiles && u.userFiles[0] && u.userFiles[0].fileName ? u.userFiles[0].fileName : u.profile;
    return file ? this.pathProfileImage + file : undefined;
  }

  /**
   * Chaves das mensagens da conversa aberta cujo texto contém o termo da
   * pesquisa, na ordem do fio. Memorizado por (array de mensagens, termo):
   * o array só muda por referência quando o contentor o altera.
   */
  get threadHits(): string[] {
    const conv = this.activeConversation;
    const messages = conv ? conv.messages : null;
    const term = normalizeSearch(this.threadSearch.trim());
    if (this.hitsCache.messages === messages && this.hitsCache.term === term) {
      return this.hitsCache.keys;
    }
    const keys: string[] = [];
    if (messages && term) {
      messages.forEach((m, index) => {
        if ((m.kind || 'text') !== 'system' && normalizeSearch(m.message || '').indexOf(term) > -1) {
          keys.push(messageKey(m, index));
        }
      });
    }
    this.hitsCache = { messages, term, keys };
    return keys;
  }

  get hitsLabel(): string {
    const hits = this.threadHits;
    if (!this.threadSearch.trim()) {
      return '';
    }
    if (hits.length === 0) {
      return this.strings.searchNoHits;
    }
    const i = this.threadHitIndex >= 0 ? this.threadHitIndex + 1 : 0;
    return this.strings.searchHits.replace('{i}', String(i)).replace('{n}', String(hits.length));
  }

  get lightboxSrc(): string | undefined {
    const lb = this.lightbox;
    const file = lb ? lb.files[lb.index] : undefined;
    return file ? (file.name || file.nameTemp) : undefined;
  }

  get lightboxName(): string {
    const lb = this.lightbox;
    const file = lb ? lb.files[lb.index] : undefined;
    return file && file.originalName ? file.originalName : '';
  }

  get lightboxCounter(): string {
    const lb = this.lightbox;
    return lb ? this.strings.imageOf.replace('{i}', String(lb.index + 1)).replace('{n}', String(lb.files.length)) : '';
  }

  private keyOf(c: { chat?: string; userId?: string; propertyReference?: string; propertyId?: string }): string {
    return c.chat ? 'chat:' + c.chat : 'user:' + (c.userId || '') + ':' + (c.propertyReference || c.propertyId || '');
  }

  private dedupeByUser(rows: ChatUser[]): ChatUser[] {
    const seen: { [id: string]: boolean } = {};
    return rows.filter(u => {
      const id = String(u.userId);
      if (seen[id]) {
        return false;
      }
      seen[id] = true;
      return true;
    });
  }

  /** Nome de um `UserApp.id`: o próprio, uma linha da lista ou um participante da conversa aberta. */
  private nameOf(userAppId: string): string | undefined {
    if (userAppId === this.meId) {
      return this.userLogged.name || undefined;
    }
    const row = this.allUsers.find(u => u.userId === userAppId);
    if (row && row.name) {
      return row.name;
    }
    const conv = this.activeConversation;
    const p = conv && conv.participants ? conv.participants.find(x => String(x.id) === userAppId) : undefined;
    return p && p.name ? p.name : undefined;
  }

  /* ---------------------------------------------------------------------
   * lista
   * ------------------------------------------------------------------ */

  initChat(): void {
    this.loadingList = true;
    if (this.usersSubscription) {
      this.usersSubscription.unsubscribe();
    }
    this.usersSubscription = this.chatService.getAllUsersChat().subscribe({
      next: (data: ChatUser[]) => {
        // A lista respondeu: se o socket ainda nao tem identidade de chat, o back-end
        // acabou de a provisionar - pede a re-resolucao (uma vez por sessao).
        if (!this.capabilities.chatIdentity && !this.identityRefreshAsked) {
          this.identityRefreshAsked = true;
          this.chatService.refreshChatIdentity();
        }
        this.allUsers = (data || []).filter(row => !!row).map(row => this.normalizeUser(row));
        // A mais recente primeiro logo ao abrir (sortChats aplica o filtro).
        this.sortChats();
        this.loadingList = false;
        if (this.active) {
          this.readActiveIfUnread();
        }
        // Conversa pedida pela notificação: abrir e consumir o estado do menu
        // (padrão de leads/calendar) para não reabrir em visitas futuras.
        const menuData: ChatMenuTarget | null = this.menuService.getData();
        if (menuData && menuData.menu === 'chat') {
          this.openFromMenu(menuData);
          this.menuService.setData(null);
        }
        this.openPendingChat();
      },
      error: () => this.loadingList = false
    });
    if (this.capabilities.canAssign) {
      this.loadQueue();
    }
    this.loadInvites();
  }

  /** `GET chat/invites` (contrato 6.3, v8): os convites que esperam pela minha resposta. */
  private loadInvites(): void {
    this.subscriptions.add(this.chatService.getInvites().subscribe(list => {
      this.invites = (list || []).filter(i => !!i && !!i.chat);
    }));
  }

  /** `GET /chat/queue`: só com canAssign. Repetido em cada `initChat()` (refresh). */
  private loadQueue(): void {
    this.queueLoaded = true;
    if (this.queueSubscription) {
      this.queueSubscription.unsubscribe();
    }
    this.queueSubscription = this.chatService.getQueue().subscribe(rows => {
      this.setQueue((rows || []).filter(row => !!row && !!row.chat).map(row => this.normalizeUser(row)));
    });
  }

  private loadQuickReplies(): void {
    this.quickLoaded = true;
    this.subscriptions.add(this.chatService.getQuickReplies().subscribe({
      next: list => this.quickReplies = (list || []).filter(r => !!r && r.status !== false && !!r.shortcut),
      error: () => this.quickReplies = []
    }));
  }

  /** A mesma normalização para as linhas da Supervisão (chat-supervision). */
  readonly normalizeRow = (row: any): ChatUser => this.normalizeUser(row);

  /**
   * Uma linha de `/chat/users` no formato da lista: `photo` a partir de `image`
   * (foto pública de `/users/files/`), `time` a partir de `dateTime`,
   * `unreadCount` sempre numérico. As chaves do back-end ficam todas.
   */
  private normalizeUser(row: any): ChatUser {
    return {
      ...row,
      photo: row.photo || (row.image ? this.pathProfileImage + row.image : undefined),
      time: row.time || row.dateTime,
      unreadCount: row.unreadCount > 0 ? row.unreadCount : 0,
      participants: Array.isArray(row.participants) ? row.participants : []
    };
  }

  private openFromMenu(data: ChatMenuTarget | null): void {
    if (!data) {
      return;
    }
    // Primeiro pelo id do chat; em recurso pelo id do utilizador (linhas de
    // colegas podem não ter `chat`). Ignora em silêncio se não encontrar.
    const entry = (data.chat ? this.allUsers.find(u => u.chat === data.chat) : undefined)
      || (data.userId ? this.allUsers.find(u => u.userId === data.userId) : undefined);
    if (entry) {
      this.getConversation(entry);
      return;
    }
    // Conversa acabada de nascer (o aviso do balão chega antes da lista a ter):
    // fica pendente e abre-se quando a lista, recarregada, a trouxer.
    if (data.chat) {
      this.pendingChatParam = String(data.chat);
      if (!this.loadingList) {
        this.initChat();
      }
    }
  }

  /** Abre a conversa de `?chat=` se já estiver na lista, e limpa o parâmetro do URL. */
  private openPendingChat(): void {
    const chat = this.pendingChatParam;
    if (!chat) {
      return;
    }
    const entry = this.allUsers.find(u => !!u.chat && u.chat === chat);
    if (!entry) {
      return;
    }
    this.pendingChatParam = null;
    this.getConversation(entry);
    if (!this.widget) {
      this.router.navigate([], { relativeTo: this.route, queryParams: { chat: null }, queryParamsHandling: 'merge', replaceUrl: true });
    }
  }

  onSearch(value: string): void {
    this.searchTerm = String(value || '').toLowerCase();
    this.applyFilter();
  }

  /** A pesquisa filtra a lista E a fila; os totais de não lidas são sempre da lista inteira. */
  private applyFilter(): void {
    const term = this.searchTerm;
    this.users = term
      ? this.allUsers.filter(u => this.matchesSearch(u, term))
      : this.allUsers.slice();
    this.queueVisible = term ? this.queue.filter(u => this.matchesSearch(u, term)) : this.queue;
    const staffRow = (u: ChatUser) => isStaffRow(u) && !u.supervised;
    const clients = this.allUsers.filter(u => !staffRow(u) && u.unreadCount > 0).length;
    const staff = this.allUsers.filter(u => staffRow(u) && u.unreadCount > 0).length;
    if (clients !== this.unreadTotals.clients || staff !== this.unreadTotals.staff) {
      this.unreadTotals = { clients, staff };
    }
  }

  private setQueue(rows: ChatUser[]): void {
    this.queue = rows;
    this.applyFilter();
  }

  private matchesSearch(u: ChatUser, term: string): boolean {
    const hay = [u.name, u.propertyReference, u.propertyTitle, u.ownerName, u.assignedName].filter(p => !!p).join(' ').toLowerCase();
    return hay.indexOf(term) > -1;
  }

  private replaceUser(target: ChatUser, patch: Partial<ChatUser>): ChatUser {
    const next: ChatUser = { ...target, ...patch };
    if (patch.dateTime !== undefined && patch.time === undefined) {
      next.time = patch.dateTime;
    }
    this.allUsers = this.allUsers.map(u => u === target ? next : u);
    this.applyFilter();
    return next;
  }

  private removeUser(target: ChatUser): void {
    this.allUsers = this.allUsers.filter(u => u !== target);
    this.applyFilter();
  }

  private sortChats(): void {
    const recency = (c: ChatUser): number | null => {
      const value = c.time || c.lastMessageAt;
      const ms = value ? new Date(value).getTime() : NaN;
      return isNaN(ms) ? null : ms;
    };
    this.allUsers = this.allUsers.slice().sort((a: ChatUser, b: ChatUser) => {
      const dateA = recency(a);
      const dateB = recency(b);
      if (dateA === null && dateB === null) {
        return 0;
      } else if (dateA === null) {
        return 1;
      } else if (dateB === null) {
        return -1;
      }
      return dateB - dateA;
    });
    this.applyFilter();
  }

  /**
   * A linha da lista a que uma mensagem do socket pertence: pelo id do chat
   * (regra da página antiga) e, sem ele, pelo utilizador e depois pelo imóvel.
   */
  private findUserFor(data: any): ChatUser | null {
    if (data && data.chat) {
      const byChat = this.allUsers.find(u => !!u.chat && u.chat === data.chat);
      if (byChat) {
        return byChat;
      }
    }
    const receiverId = data && data.userReceiver && data.userReceiver.id;
    const senderId = data && data.userSender && data.userSender.id;
    const candidates = this.allUsers.filter(u => !u.supervised && (u.userId === receiverId || u.userId === senderId));
    if (candidates.length > 1) {
      return candidates.find(us => us.chat === data.chat || (!us.chat && us.propertyReference === data.propertyReference)) || null;
    }
    return candidates.length > 0 ? candidates[0] : null;
  }

  /* ---------------------------------------------------------------------
   * fila "Por atribuir" (só com canAssign)
   * ------------------------------------------------------------------ */

  private removeFromQueue(chat: string): void {
    if (this.queue.some(q => q.chat === chat)) {
      this.setQueue(this.queue.filter(q => q.chat !== chat));
    }
  }

  /**
   * Mantém a fila em tempo real a partir de `receive_message`: uma mensagem
   * do cliente numa conversa sem responsável entra (se ainda não estava); uma
   * resposta da equipa tira-a. Notas, sistema e cartões de estado não contam
   * (NON_CONVERSATIONAL_KINDS, a mesma lista do back-end); anexos contam.
   */
  private syncQueue(entry: ChatUser, data: any): void {
    if (!this.capabilities.canAssign || !entry.chat || !isClientRow(entry)) {
      return;
    }
    const kind = data.kind || 'text';
    if (NON_CONVERSATIONAL_KINDS.indexOf(kind) > -1) {
      return;
    }
    const chat = entry.chat;
    if (data.userSender.id === entry.userId) {
      if (!entry.assignedUserAppId && !this.queue.some(q => q.chat === chat)) {
        this.setQueue([...this.queue, { ...entry, waitingSince: data.time }]
          .sort((a, b) => new Date(a.waitingSince).getTime() - new Date(b.waitingSince).getTime()));
      }
    } else {
      this.removeFromQueue(chat);
    }
  }

  /* ---------------------------------------------------------------------
   * conversa
   * ------------------------------------------------------------------ */

  getConversation(userClicked: ChatUser): void {
    // Uma linha da fila é a mesma conversa que (talvez) já está na lista: a
    // entrada da lista manda, para as duas ficarem coerentes.
    const listed = userClicked.chat ? this.allUsers.find(u => !!u.chat && u.chat === userClicked.chat) : undefined;
    const target = listed || userClicked;
    const current = this.activeConversation;
    this.assignOpen = false;
    if (!target.chat) {
      this.setActive({ ...target, messages: [] });
    } else if (!current || target.chat !== current.chat) {
      this.loadingMessages = true;
      const chatId = target.chat;
      this.setActive({ ...target, messages: [] });
      this.subscriptions.add(this.chatService.getAllMessages(chatId).subscribe({
        next: (history: ChatMessage[]) => {
          const active = this.activeConversation;
          if (!active || active.chat !== chatId) {
            // Entretanto abriu-se outra conversa.
            return;
          }
          const messages = (history || []).map(m => this.normalizeMessage(m));
          this.patchActive({ messages, unreadCount: 0 });
          const entry = this.allUsers.find(us => us.chat === chatId && us.userId === target.userId);
          if (entry) {
            this.replaceUser(entry, { unreadCount: 0 });
          }
          const unread = messages.some(m => m.unread === true && m.userReceiver && m.userReceiver.id === this.meId);
          if (unread) {
            this.chatService.readMessage({ chat: chatId });
          }
          this.loadingMessages = false;
        },
        error: () => this.loadingMessages = false
      }));
    }
  }

  closeConversation(): void {
    this.setActive(null);
    this.assignOpen = false;
  }

  private setActive(conv: ChatConversation | null): void {
    const prev = this.activeConversation;
    const changed = !prev || !conv || this.keyOf(prev) !== this.keyOf(conv);
    if (changed) {
      // O que era da conversa anterior fecha com ela: qualquer modal aberto (visita,
      // qualificação, transferência, seletor de imóvel, lightbox) e o seu estado, e o
      // "a escrever…" que o compositor ainda tivesse ligado nela.
      this.closeModal();
      this.resetModalState();
      if (prev && prev.chat && !prev.supervised) {
        this.chatService.sendTyping({ chat: prev.chat, typing: false });
      }
    }
    this.loadDealContext(conv);
    if (changed) {
      this.loadPendingInvites(conv);
    }
    this.activeConversation = conv;
    this.typingActive = false;
    this.clearTypingTimer();
    // A pesquisa na conversa e o lightbox são da conversa anterior.
    this.threadSearchOpen = false;
    this.threadSearch = '';
    this.threadHitIndex = -1;
    this.threadFocusKey = null;
    this.lightbox = null;
    this.participantNames = conv ? this.otherStaffNames(conv) : [];
    // A ficha do imóvel no CRM: a lista de imóveis abre o imóvel pedido em
    // `?openPropertyId=` (id numérico ou uuid).
    const id = conv ? (conv.propertyId || conv.uniqueId) : undefined;
    this.propertyRoute = conv && id ? ['/properties'] : null;
    this.propertyQueryParams = conv && id ? { openPropertyId: String(id) } : null;
  }

  /** O estado dos modais pertence à conversa em que foram abertos. */
  private resetModalState(): void {
    this.editingMessage = null;
    this.replyingTo = null;
    this.visitBusy = false;
    this.visitActionBusy = null;
    this.visitRequestMessageId = null;
    this.visitProperty = null;
    this.visitReschedule = null;
    this.agendaPicksSeq++;
    this.visitFormBusy = null;
    this.propertyReturn = null;
    this.openQualAnswersOnEcho = false;
    this.propertyPickMode = 'send';
    this.propertyFilters = null;
    this.qualEditing = false;
    this.qualMessage = null;
    this.transferUserId = null;
  }

  /** O balão voltou a abrir (ou a lista chegou): o que ficou por ler na conversa aberta lê-se agora. */
  private readActiveIfUnread(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat) {
      return;
    }
    const me = this.meId;
    if (conv.messages.some(m => m.unread === true && !!m.userReceiver && m.userReceiver.id === me)) {
      this.chatService.readMessage({ chat: conv.chat });
    }
    const entry = this.allUsers.find(u => !!u.chat && u.chat === conv.chat);
    if (entry && entry.unreadCount > 0) {
      this.replaceUser(entry, { unreadCount: 0 });
    }
    if (conv.unreadCount > 0) {
      this.patchActive({ unreadCount: 0 });
    }
  }

  private patchActive(patch: Partial<ChatConversation>): void {
    if (this.activeConversation) {
      this.activeConversation = { ...this.activeConversation, ...patch };
      if (patch.participants !== undefined) {
        this.participantNames = this.otherStaffNames(this.activeConversation);
      }
    }
  }

  /** Participantes que não são nem o próprio nem o interlocutor da linha: os colegas convidados. */
  private otherStaffNames(conv: ChatConversation): string[] {
    return (conv.participants || [])
      .filter(p => String(p.id) !== this.meId && String(p.id) !== String(conv.userId) && !!p.name)
      .map(p => String(p.name));
  }

  private mapMessages(fn: (m: ChatMessage) => ChatMessage): void {
    if (this.activeConversation) {
      this.patchActive({ messages: this.activeConversation.messages.map(fn) });
    }
  }

  /** Uma mensagem do socket pertence à conversa no ecrã? */
  private isForActive(data: any, entry: ChatUser): boolean {
    const conv = this.activeConversation;
    if (!conv) {
      return false;
    }
    if (conv.chat) {
      return conv.chat === (entry.chat || data.chat);
    }
    const userMatch = conv.userId === data.userReceiver.id || conv.userId === data.userSender.id;
    if (!userMatch) {
      return false;
    }
    // Ainda sem id de chat: o mesmo utilizador noutro imóvel é outra conversa.
    if (conv.propertyReference && entry.propertyReference && conv.propertyReference !== entry.propertyReference) {
      return false;
    }
    return true;
  }

  /** `kind` por omissão é text; `files` é sempre um array, com o URL do anexo resolvido (contrato 6.2 / 3). */
  private normalizeMessage(data: any): ChatMessage {
    const files = Array.isArray(data.files) ? data.files : [];
    return {
      ...data,
      kind: data.kind || 'text',
      system: data.system ? { ...data.system, params: this.withSystemNames(data.system.params) } : data.system,
      property: data.property ? this.withPropertyRoute(data.property) : undefined,
      files: files.map((f: any) => f && f.name && !this.isResolvedUrl(f.name) ? { ...f, name: this.fileUrl(f) } : f)
    };
  }

  /**
   * Mensagens de sistema (`{byName} convidou {name}`): quando o servidor não
   * manda o nome, resolve-se pelo id a partir dos utilizadores conhecidos (o
   * próprio incluído). Nunca fica um UUID no texto.
   */
  private withSystemNames(params: any): any {
    if (!params || typeof params !== 'object') {
      return params;
    }
    const out = { ...params };
    // nameOf() conhece o próprio, a lista e os participantes da conversa aberta.
    const nameOf = (id: any): string | undefined => (id ? this.nameOf(String(id)) : undefined);
    if (!out.byName && out.by) {
      out.byName = nameOf(out.by) || '';
    }
    if (!out.name && out.userAppId) {
      out.name = nameOf(out.userAppId) || '';
    }
    if (!out.fromName && out.from) {
      out.fromName = nameOf(out.from) || '';
    }
    return out;
  }

  /**
   * `kind: 'property'`: o cartão abre a ficha do imóvel no CRM, e `image`
   * (nome do ficheiro em `properties_files`, contrato 3) passa a URL do
   * endpoint de imagens de imóveis do back-end.
   */
  private withPropertyRoute(property: any): any {
    if (!property || property.route) {
      return property;
    }
    // `imageFile` (rtn, 2026-10-26): o nome do ficheiro, resolvido com a base do
    // CRM como no histórico; não depende de PROPERTY_IMG_URL no rtn.
    const image = property.imageFile && !this.isResolvedUrl(String(property.imageFile))
      ? this.pathPropertyImage + property.imageFile
      : property.image && !this.isResolvedUrl(String(property.image))
        ? this.pathPropertyImage + property.image
        : property.image;
    return property.id !== undefined && property.id !== null
      ? { ...property, image, route: ['/properties'], queryParams: { openPropertyId: String(property.id) } }
      : { ...property, image };
  }

  private isResolvedUrl(name: string): boolean {
    return name.indexOf('http') === 0 || name.indexOf('data:') === 0 || name.indexOf('/') === 0;
  }

  /* ---------------------------------------------------------------------
   * eventos do socket
   * ------------------------------------------------------------------ */

  private listenSocket(): void {
    this.subscriptions.add(this.chatService.viewedMessage().subscribe(data => this.onViewed(data)));
    this.subscriptions.add(this.chatService.viewedMyMessage().subscribe(() => this.notifyTopbar()));
    this.subscriptions.add(this.chatService.receiveMessage().subscribe(data => this.receiveMessage(data)));
    // Secção 12: reações, apagar para mim, enviar mais tarde.
    this.subscriptions.add(this.chatService.receiveMessageReaction().subscribe(data => this.onMessageReaction(data)));
    this.subscriptions.add(this.chatService.receiveMessageHidden().subscribe(data => this.onMessageHidden(data)));
    this.subscriptions.add(this.chatService.scheduledMessages().subscribe(data => this.onScheduledMessages(data)));
    this.subscriptions.add(this.chatService.scheduledMessageFailed().subscribe(() => this.errorMsg(this.strings.scheduledFailed)));
    this.subscriptions.add(this.chatService.receiveMessageFile().subscribe(data => this.receiveMessageFile(data)));
    this.subscriptions.add(this.chatService.receiveDeleteChat().subscribe(data => this.receiveDeleteChat(data)));
    this.subscriptions.add(this.chatService.receiveMessageDelete().subscribe(data => this.receiveDeleteMessage(data)));
    this.subscriptions.add(this.chatService.receiveMessageDeleteFiles().subscribe(data => this.receiveDeleteFiles(data)));
    this.subscriptions.add(this.chatService.chatAssigned().subscribe(data => this.onChatAssigned(data)));
    this.subscriptions.add(this.chatService.chatParticipantAdded().subscribe(data => this.onParticipantAdded(data)));
    // Contrato 6.3, v8: convites por aceitar.
    this.subscriptions.add(this.chatService.chatInvite().subscribe(data => this.onInviteReceived(data)));
    this.subscriptions.add(this.chatService.chatInviteSent().subscribe(data => this.onInviteSent(data)));
    this.subscriptions.add(this.chatService.chatInviteAnswered().subscribe(data => this.onInviteAnswered(data)));
    this.subscriptions.add(this.chatService.chatInviteCancelled().subscribe(data => this.onInviteCancelled(data)));
    this.subscriptions.add(this.chatService.chatParticipantRemoved().subscribe(data => this.onParticipantRemoved(data)));
    this.subscriptions.add(this.chatService.chatTyping().subscribe(data => this.onChatTyping(data)));
    this.subscriptions.add(this.chatService.receiveMessageUpdate().subscribe(data => this.onMessageUpdate(data)));
    this.subscriptions.add(this.chatService.receiveMessageEdit().subscribe(data => this.onMessageEdit(data)));
    this.subscriptions.add(this.chatService.error().subscribe(body => this.onError(body)));
  }

  /** `receive_message_update` (7.1): o estado de um cartão da conversa aberta mudou. */
  private onMessageUpdate(data: any): void {
    const conv = this.activeConversation;
    if (!conv || !data || !data.messageId || (conv.chat && data.chat && conv.chat !== data.chat)) {
      return;
    }
    this.mapMessages(m => m.messageId === data.messageId ? { ...m, params: data.params || m.params } : m);
    this.settleToast('update:' + data.messageId);
    // O cliente respondeu à qualificação: procuram-se já os imóveis que encaixam (fase 3).
    if (data.kind === 'qualification' && data.params && data.params.status === 'answered') {
      this.loadMatchingProperties(this.activeConversation);
    }
    if (data.kind === 'visit_request' && data.params && (data.params.status === 'scheduled' || data.params.status === 'scheduling')) {
      // A visita ficou marcada do lado do servidor: o contexto do negócio mudou.
      this.loadDealContext(this.activeConversation);
    }
  }

  /** Um toast de sucesso que só se mostra quando o servidor ecoar o resultado (ver `awaitingToasts`). */
  private expectToast(key: string, text: string): void {
    this.awaitingToasts[key] = text;
  }

  private settleToast(key: string): void {
    const text = this.awaitingToasts[key];
    if (text) {
      delete this.awaitingToasts[key];
      this.toast(text);
    }
  }

  /** A chave de `awaitingToasts` de uma mensagem ecoada: `system:<code>` ou o kind. */
  private toastKeyOf(data: any): string {
    const kind: string = data.kind || 'text';
    return kind === 'system' && data.system && data.system.code ? 'system:' + data.system.code : kind;
  }

  /** Pré-visualização de uma linha de sistema na lista (o texto, nunca o código). */
  private systemPreview(data: any): string {
    const sys = data.system || {};
    return sys.code ? formatSystemText(sys.code, this.withSystemNames(sys.params), this.strings) : '';
  }

  /** A topbar recarrega o menu de mensagens quando estamos em /chat (como a página antiga). */
  private notifyTopbar(): void {
    this.eventService.broadcast('update-notifications-messages');
  }

  private onViewed(data: any): void {
    const conv = this.activeConversation;
    if (!conv || !data || conv.chat !== data.chat) {
      return;
    }
    const me = this.meId;
    this.mapMessages(m => m.unread && m.userSender && m.userSender.id === me ? { ...m, unread: false } : m);
  }

  /**
   * Recusas (contrato 4): decididas pela MARCA, nunca pelo texto. Um envio
   * recusado (`createMsg`) ou um socket sem identidade (`auth`) quer dizer que
   * a mensagem pendente mais antiga não vai a lado nenhum. As marcas da
   * versão 2 têm cada uma o seu aviso; `typing` falha em silêncio.
   */
  private onError(body: any): void {
    if (!body || typeof body !== 'object') {
      return;
    }
    if (body.createMsg === true || body.auth === true) {
      this.failOldestPending();
    }
    if (body.assignChat === true) {
      this.assignBusy = false;
      this.errorMsg(this.strings.assignFailed);
    }
    if (body.inviteChat === true) {
      if (body.message === 'Invite not found') {
        // O convite já foi respondido, cancelado ou nunca existiu: a lista acerta-se.
        this.inviteBusy = null;
        this.errorMsg(this.strings.inviteGone);
        this.loadInvites();
      } else {
        this.errorMsg(this.strings.inviteFailed);
      }
    }
    if (body.qualification === true) {
      this.errorMsg(this.strings.qualificationFailed);
    }
    if (body.reaction === true || body.hideMsg === true) {
      this.errorMsg(this.strings.reactionFailed);
      return;
    }
    if (body.schedule === true) {
      this.errorMsg(this.strings.scheduleFailed);
      return;
    }
    if (body.deleteMsg === true) {
      this.errorMsg(body.code === 'window_expired' ? this.strings.deleteExpired : this.strings.messageDeleteFailed);
    }
    if (body.editMsg === true) {
      // A edição já estava no ecrã: volta o texto anterior.
      const pending = this.pendingEdits;
      this.pendingEdits = {};
      this.mapMessages(m => m.messageId && pending[m.messageId]
        ? { ...m, message: pending[m.messageId].message, editedAt: pending[m.messageId].editedAt }
        : m);
      this.errorMsg(body.code === 'window_expired' ? this.strings.editExpired : this.strings.editFailed);
    }
    if (body.transferChat === true) {
      this.errorMsg(this.strings.transferFailed);
    }
    if (body.participants === true) {
      this.errorMsg(this.strings.participantsFailed);
    }
    if (body.visitRequest === true) {
      this.visitBusy = false;
      this.errorMsg(this.strings.visitFailed);
    }
    if (body.announce === true) {
      this.errorMsg(this.strings.announceFailed);
    }
    // Uma recusa de qualquer ação anula os toasts de sucesso que esperavam pelo eco.
    this.awaitingToasts = {};
    if (body.setPresence === true) {
      // O servidor não aceitou o `away`: o botão volta a "Disponível".
      this.chatService.myPresence$.next('online');
      this.errorMsg(this.strings.presenceFailed);
    }
  }

  /**
   * Conversa nova que não está na lista. O back-end do CRM manda a linha
   * pronta em `chatUser` (cliente do site/app); senão pede-se a lista de novo.
   */
  private adoptUnknown(data: any): ChatUser | null {
    if (data.chatUser && isClientRow(data.chatUser)) {
      const entry = this.normalizeUser({ ...data.chatUser, chat: data.chatUser.chat || data.chat });
      this.allUsers = [entry, ...this.allUsers];
      this.applyFilter();
      return entry;
    }
    this.initChat();
    return null;
  }

  private receiveMessage(data: any): void {
    if (!data || !data.userReceiver || !data.userSender) {
      return;
    }
    const entry = this.findUserFor(data) || this.adoptUnknown(data);
    if (!entry) {
      this.notifyTopbar();
      return;
    }
    const entryChat = entry.chat || data.chat;
    const forActive = this.isForActive(data, entry);
    // Uma mensagem já no fio (cartão re-anunciado, eco repetido) só atualiza o fio:
    // a lista não muda de pré-visualização nem de ordem.
    const known = forActive && this.isKnownMessage(data.messageId);
    if (forActive) {
      this.absorbMessage(this.normalizeMessage(data));
      if (data.userSender.id !== this.meId) {
        // Uma mensagem do outro lado encerra o "a escrever…".
        this.typingActive = false;
        this.clearTypingTimer();
      }
      if (data.userSender.id === this.meId || (data.kind || 'text') === 'system') {
        this.settleToast(this.toastKeyOf(data));
      }
      // "Responder eu": o cartão de qualificação acabou de ecoar, abrem-se as respostas.
      if (this.openQualAnswersOnEcho && data.kind === 'qualification' && data.userSender.id === this.meId && !known) {
        this.openQualAnswersOnEcho = false;
        const conv = this.activeConversation;
        const card = conv ? conv.messages.find(x => x.messageId === data.messageId) : undefined;
        if (conv && card) {
          this.openQualAnswers(conv, card);
        }
      }
    }
    if (known) {
      return;
    }
    const kind: ChatMessageKind = data.kind || 'text';
    const patch: Partial<ChatUser> = {
      message: kind === 'system' ? this.systemPreview(data) : data.message,
      time: data.time, dateTime: data.time, filesNumber: data.filesNumber, lastMessageKind: kind, lastMessageDeleted: false
    };
    if (!entry.chat) {
      patch.chat = data.chat;
    }
    if (forActive && this.active) {
      if (this.meId === data.userReceiver.id && data.message !== '') {
        this.chatService.readMessage({ chat: entryChat });
      }
    } else if (entry.userId === data.userSender.id || (forActive && data.userSender.id !== this.meId)) {
      // Fora da conversa aberta, ou com o balão escondido: fica por ler e conta.
      patch.unreadCount = (entry.unreadCount || 0) + 1;
      if (forActive) {
        this.patchActive({ unreadCount: patch.unreadCount });
      }
    }
    this.syncQueue(entry, data);
    this.replaceUser(entry, patch);
    this.sortChats();
    this.notifyTopbar();
  }

  /** A mensagem (por `messageId`) já está no fio da conversa aberta. */
  private isKnownMessage(messageId: any): boolean {
    const conv = this.activeConversation;
    return !!messageId && !!conv && conv.messages.some(m => !!m.messageId && m.messageId === messageId);
  }

  /** Mete uma mensagem do servidor na conversa aberta: resolve a cópia pendente, atualiza uma conhecida, ou acrescenta. */
  private absorbMessage(incoming: ChatMessage): void {
    const conv = this.activeConversation;
    if (!conv) {
      return;
    }
    let messages = conv.messages;
    const known = incoming.messageId ? messages.find(m => !!m.messageId && m.messageId === incoming.messageId) : undefined;
    if (known) {
      messages = messages.map(m => m === known ? { ...m, ...incoming } : m);
    } else {
      const pending = this.matchPending(messages, incoming);
      if (pending) {
        this.clearSendTimer(pending);
        messages = messages.map(m => m === pending ? this.confirmPending(pending, incoming) : m);
      } else {
        messages = [...messages, incoming];
      }
    }
    this.activeConversation = { ...conv, chat: conv.chat || incoming.chat, messages };
  }

  /**
   * O eco substitui a mensagem otimista. Um cartão de imóvel guarda a foto que
   * já tinha (a do modal de seleção) quando o eco vem sem ela.
   */
  private confirmPending(pending: ChatMessage, incoming: ChatMessage): ChatMessage {
    const confirmed: ChatMessage = { ...incoming, clientMessageId: pending.clientMessageId };
    if (incoming.property && !incoming.property.image && pending.property && pending.property.image) {
      confirmed.property = { ...incoming.property, image: pending.property.image };
    }
    return confirmed;
  }

  /**
   * O eco do servidor de algo que este utilizador enviou. Por clientMessageId,
   * que o servidor devolve (contrato 6.4); em recurso por tipo + texto, a
   * mais antiga primeiro (mensagens enviadas por um cliente antigo).
   */
  private matchPending(messages: ChatMessage[], incoming: ChatMessage): ChatMessage | undefined {
    if (!incoming.userSender || incoming.userSender.id !== this.meId) {
      return undefined;
    }
    if (incoming.clientMessageId) {
      const byId = messages.find(m => !!m.sendState && m.clientMessageId === incoming.clientMessageId);
      if (byId) {
        return byId;
      }
    }
    const kind = incoming.kind || 'text';
    return messages.find(m => m.sendState === 'sending' && (m.kind || 'text') === kind && m.message === incoming.message
      && !(m.files && m.files.length > 0)
      && (kind !== 'property' || !m.property || !incoming.property || String(m.property.id) === String(incoming.property.id)));
  }

  private receiveMessageFile(data: any): void {
    if (!data || !data.userReceiver || !data.userSender) {
      return;
    }
    const entry = this.findUserFor(data) || this.adoptUnknown(data);
    if (!entry) {
      this.notifyTopbar();
      return;
    }
    const entryChat = entry.chat || data.chat;
    const forActive = this.isForActive(data, entry);
    const known = forActive && this.isKnownMessage(data.messageId);
    if (forActive) {
      this.absorbFileMessage(data);
    }
    if (known) {
      return;
    }
    const patch: Partial<ChatUser> = {
      message: data.message, time: data.time, dateTime: data.time, filesNumber: data.filesNumber, lastMessageKind: data.kind || 'text',
      lastMessageDeleted: false,
      lastMessageAudio: Array.isArray(data.files) && data.files.some((f: any) => f && typeof f.type === 'string' && f.type.indexOf('audio/') === 0)
    };
    if (!entry.chat && data.chat) {
      patch.chat = data.chat;
    }
    if (forActive && this.active) {
      if (this.meId === data.userReceiver.id && data.message === '') {
        this.chatService.readMessage({ chat: entryChat });
      }
    } else if (entry.userId === data.userSender.id || (forActive && data.userSender.id !== this.meId)) {
      patch.unreadCount = (entry.unreadCount || 0) + 1;
      if (forActive) {
        this.patchActive({ unreadCount: patch.unreadCount });
      }
    }
    // Um anexo do cliente é uma pergunta como outra qualquer: entra na fila.
    this.syncQueue(entry, data);
    this.replaceUser(entry, patch);
    this.sortChats();
    this.notifyTopbar();
  }

  private absorbFileMessage(data: any): void {
    const conv = this.activeConversation;
    if (!conv) {
      return;
    }
    let messages = conv.messages;
    // Idempotente por messageId: quem envia recebe esta mensagem duas vezes,
    // primeiro na resposta HTTP do upload e depois no eco do servidor.
    const known = data.messageId ? messages.find(m => !!m.messageId && m.messageId === data.messageId) : undefined;
    // O eco não traz idTemp (o anúncio é só { messageId }), e
    // `undefined === undefined` apanhava qualquer mensagem já resolvida.
    const hasIdTemp = data.idTemp !== undefined && data.idTemp !== null && data.idTemp !== '';
    const placeholder = !known && hasIdTemp
      ? messages.find(m => m.idTemp !== undefined && m.idTemp !== null && String(m.idTemp) === String(data.idTemp))
      : undefined;
    if (known) {
      messages = messages.map(m => m === known ? { ...m, unread: data.unread } : m);
    } else if (placeholder && conv.userId === placeholder.userReceiver.id) {
      this.clearSendTimer(placeholder);
      delete this.pendingFiles[String(placeholder.idTemp)];
      const files = (placeholder.files || []).map((f: ChatMessageFile, index: number) => {
        // Por `order`; por posição quando o servidor os numera de outra forma.
        const update = (data.files || []).find((x: any) => x.order === f.order) || (data.files || [])[index];
        if (!update) {
          return f;
        }
        return {
          ...f,
          id: update.id,
          name: this.fileUrl(update),
          isLoad: update.isLoad,
          originalName: update.originalName,
          sizeText: update.sizeText,
          nameTemp: ''
        };
      });
      const settled: ChatMessage = {
        ...placeholder,
        chat: data.chat,
        messageId: data.messageId,
        unread: data.unread,
        time: data.time,
        isShowFiles: data.isShowFiles,
        idTemp: undefined,
        sendState: undefined,
        files
      };
      messages = messages.map(m => m === placeholder ? settled : m);
    } else {
      messages = [...messages, this.normalizeMessage(data)];
    }
    this.activeConversation = { ...conv, chat: conv.chat || data.chat, messages };
  }

  /** `files[].name` é um nome de ficheiro do servidor; a imagem é servida pelo endpoint de anexos (contrato 3). */
  private fileUrl(file: any): string {
    const type: string[] = String(file.type || '').split('/');
    return `${this.pathFile}${file.name}.${type[1] || ''}`;
  }

  private receiveDeleteChat(data: any): void {
    if (!data) {
      return;
    }
    const entry = this.allUsers.find(e => !!e.chat && e.chat === data.chat);
    if (!entry) {
      return;
    }
    if (this.activeConversation && this.activeConversation.chat === data.chat) {
      this.closeConversation();
      if (data.userId === this.meId) {
        this.notice(this.strings.deletedTitle, this.strings.deletedChat);
      }
    }
    this.removeFromQueue(String(data.chat));
    // Regra da página antiga: a conversa de um cliente desaparece; um colega
    // fica na lista como contacto para começar de novo.
    if (isClientRow(entry) || entry.supervised) {
      this.removeUser(entry);
    } else {
      this.replaceUser(entry, {
        message: undefined, time: undefined, dateTime: undefined, filesNumber: undefined, unreadCount: 0, chat: undefined,
        assignedUserAppId: null, assignedName: undefined, participants: []
      });
    }
    this.notifyTopbar();
  }

  /**
   * `receive_message_delete` (secção 9): apagada para todos. Como no WhatsApp a
   * mensagem não desaparece: fica "Esta mensagem foi apagada" no lugar dela, e a
   * lista mostra "Mensagem apagada" se era a última.
   */
  private receiveDeleteMessage(data: any): void {
    if (!data || !data.messageId) {
      this.notifyTopbar();
      return;
    }
    this.markDeleted(data.chat, data.messageId, data.deletedAt, data.time);
    this.notifyTopbar();
  }

  /** A mensagem passa a "apagada" no fio (se for da conversa aberta) e, se era a última, na lista. */
  private markDeleted(chat: string, messageId: string, deletedAt?: any, time?: any): void {
    const conv = this.activeConversation;
    let wasLast = false;
    if (conv && conv.chat === chat) {
      const last = conv.messages.length > 0 ? conv.messages[conv.messages.length - 1] : undefined;
      wasLast = !!last && last.messageId === messageId;
      // As citações desta mensagem passam também a "apagada".
      this.mapMessages(m => m.messageId === messageId ? this.asDeleted(m, deletedAt)
        : m.replyTo && m.replyTo.messageId === messageId ? { ...m, replyTo: { ...m.replyTo, deleted: true, text: '', files: 0 } } : m);
      if (this.editingMessage && this.editingMessage.messageId === messageId) {
        this.editingMessage = null;
      }
    }
    this.patchEntryIfLast(chat, wasLast, time, { message: undefined, filesNumber: undefined, lastMessageDeleted: true });
  }

  private asDeleted(m: ChatMessage, deletedAt?: any): ChatMessage {
    return {
      ...m, deleted: true, deletedAt: deletedAt || new Date(), editedAt: undefined, message: '', files: [],
      property: undefined, params: undefined, mentions: undefined, system: undefined
    };
  }

  /**
   * Atualiza a linha da lista quando a mensagem mudada era a última: pela conversa
   * aberta (`wasLast`) ou pela hora de envio (`time`, que o servidor manda) igual à
   * da última mensagem da linha.
   */
  private patchEntryIfLast(chat: string, wasLast: boolean, time: any, patch: Partial<ChatUser>): void {
    const entry = this.allUsers.find(u => !!u.chat && u.chat === chat);
    if (!entry) {
      return;
    }
    const sameTime = !!time && !!entry.time && new Date(entry.time).getTime() === new Date(time).getTime();
    if (wasLast || sameTime) {
      this.replaceUser(entry, patch);
    }
  }

  /** `receive_message_edit` (secção 9): o texto novo, e "Editada" junto da hora. */
  private onMessageEdit(data: any): void {
    if (!data || !data.messageId) {
      return;
    }
    delete this.pendingEdits[data.messageId];
    const conv = this.activeConversation;
    let wasLast = false;
    if (conv && conv.chat === data.chat) {
      const last = conv.messages.length > 0 ? conv.messages[conv.messages.length - 1] : undefined;
      wasLast = !!last && last.messageId === data.messageId;
      this.mapMessages(m => m.messageId === data.messageId && !m.deleted
        ? { ...m, message: data.message, editedAt: data.editedAt || m.editedAt || new Date() }
        : m.replyTo && m.replyTo.messageId === data.messageId
          ? { ...m, replyTo: { ...m.replyTo, text: String(data.message || '').slice(0, 200) } }
          : m);
    }
    this.patchEntryIfLast(data.chat, wasLast, data.time, { message: data.message });
  }

  /* ---- responder (secção 10, como no WhatsApp) ---- */

  /** Responder só numa conversa com id, não supervisionada, e com um servidor que conhece respostas (contrato ≥ 6). */
  get canReplyHere(): boolean {
    const conv = this.activeConversation;
    return !!conv && !!conv.chat && !conv.supervised && (this.capabilities.contractVersion || 0) >= 6;
  }

  /** O nome da faixa "A responder a …": "Você" numa mensagem minha. */
  get replyName(): string {
    const m = this.replyingTo;
    if (!m || !m.userSender) {
      return '';
    }
    return m.userSender.id === this.meId ? this.strings.participantYou : (m.userSender.name || '');
  }

  /** "Responder" (botão ou arrastar para a direita): o compositor mostra a faixa e foca. */
  onReplyMessage(m: ChatMessage): void {
    if (!this.canReplyHere || !m || !m.messageId || m.deleted || m.kind === 'system') {
      return;
    }
    this.editingMessage = null;
    this.replyingTo = m;
  }

  onReplyCancel(): void {
    this.replyingTo = null;
  }

  /** A citação local de uma resposta acabada de escrever (o eco do servidor traz a dele). */
  private quoteOf(m: ChatMessage): ChatMessageReply {
    return {
      messageId: String(m.messageId),
      authorId: m.userSender ? m.userSender.id : undefined,
      authorName: m.userSender ? m.userSender.name : undefined,
      kind: m.kind || 'text',
      text: (m.message || '').slice(0, 200),
      files: (m.files || []).length,
      deleted: !!m.deleted
    };
  }

  /** Clique na citação: o fio desloca-se até à mensagem original e realça-a por um instante. */
  onJumpTo(messageId: string): void {
    const conv = this.activeConversation;
    if (!conv) {
      return;
    }
    const index = conv.messages.findIndex(m => m.messageId === messageId);
    if (index === -1) {
      return;
    }
    const key = messageKey(conv.messages[index], index);
    this.threadFocusKey = null;
    if (this.jumpTimer) {
      clearTimeout(this.jumpTimer);
    }
    setTimeout(() => {
      this.threadFocusKey = key;
      this.jumpTimer = setTimeout(() => {
        this.jumpTimer = null;
        if (!this.threadSearchOpen && this.threadFocusKey === key) {
          this.threadFocusKey = null;
        }
      }, 1600);
    }, 0);
  }

  /* ---- editar (secção 9, como no WhatsApp) ---- */

  /** "Editar" numa mensagem minha: o compositor passa a modo edição (até 15 min). */
  onEditMessage(m: ChatMessage): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || conv.supervised || !m || !m.messageId || m.deleted) {
      return;
    }
    if (!withinWindow(m.time, this.capabilities.messageEditWindowSeconds)) {
      this.errorMsg(this.strings.editExpired);
      return;
    }
    this.replyingTo = null;
    this.editingMessage = m;
  }

  onEditCancel(): void {
    this.editingMessage = null;
  }

  /** Mostra já o texto novo (com "Editada") e pede ao servidor; se recusar, volta o anterior. */
  onEditSubmit(event: { messageId: string; text: string }): void {
    const conv = this.activeConversation;
    this.editingMessage = null;
    const target = conv ? conv.messages.find(m => m.messageId === event.messageId) : undefined;
    if (!conv || !conv.chat || !target || target.deleted) {
      return;
    }
    if (!withinWindow(target.time, this.capabilities.messageEditWindowSeconds)) {
      this.errorMsg(this.strings.editExpired);
      return;
    }
    this.pendingEdits[event.messageId] = { message: target.message || '', editedAt: target.editedAt };
    this.mapMessages(m => m.messageId === event.messageId ? { ...m, message: event.text, editedAt: new Date() } : m);
    this.chatService.editMessage({ chat: conv.chat, messageId: event.messageId, message: event.text });
  }

  private receiveDeleteFiles(data: any): void {
    const conv = this.activeConversation;
    if (!data || !conv || conv.chat !== data.chat) {
      this.notifyTopbar();
      return;
    }
    // Idempotente: quem apaga recebe isto duas vezes (resposta HTTP, depois o
    // eco do servidor). Não haver nada para tirar quer dizer que já foi aplicado.
    const index = conv.messages.findIndex(m => m.messageId === data.messageId);
    if (index === -1) {
      return;
    }
    const target = conv.messages[index];
    if (!data.deleteMessage && data.fileId && !(target.files || []).some(f => f.id === data.fileId)) {
      return;
    }
    const mine = !!target.userSender && target.userSender.id === this.meId;
    if (data.deleteMessage) {
      // Saiu o último anexo: a mensagem fica como "apagada para todos" (secção 9).
      this.markDeleted(data.chat, data.messageId);
    } else if (data.fileId) {
      this.mapMessages(m => m === target ? { ...m, files: (m.files || []).filter(f => f.id !== data.fileId) } : m);
    }
    if (mine) {
      this.notice(this.strings.deletedTitle, this.strings.deletedFile);
    }
    this.notifyTopbar();
  }

  /* ---------------------------------------------------------------------
   * atribuição (POST /chat/:chat/assign + chat_assigned)
   * ------------------------------------------------------------------ */

  /** "Atribuir a consultor" na faixa: abre/fecha o painel. */
  toggleAssignPanel(): void {
    this.assignOpen = !this.assignOpen;
  }

  /** "Assumir eu": o próprio `userAppId` no corpo (o servidor trata "assumir" por aí). */
  onTakeOver(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.meId) {
      return;
    }
    this.requestAssign(conv.chat, this.meId);
  }

  /** "Atribuir" numa linha do painel. */
  onAssign(consultant: ChatUser): void {
    const conv = this.activeConversation;
    this.assignOpen = false;
    if (!conv || !conv.chat || !consultant.userId) {
      return;
    }
    this.requestAssign(conv.chat, consultant.userId);
  }

  private requestAssign(chat: string, userAppId: string): void {
    if (this.assignBusy) {
      return;
    }
    this.assignBusy = true;
    this.subscriptions.add(this.chatService.assignChat(chat, userAppId).subscribe({
      next: (result: ChatAssignResult) => {
        this.assignBusy = false;
        const target = (result && result.assignedUserAppId) || userAppId;
        this.applyAssignment(chat, target, result && result.assignedName, result && result.assignedAt);
        if (result && result.systemMessage) {
          this.absorbAssignSystemMessage(chat, result);
        }
      },
      error: (err: any) => {
        this.assignBusy = false;
        this.errorMsg(this.apiError.resolve(err, this.strings.assignFailed).message);
      }
    }));
  }

  /**
   * `chat_assigned { chat, userAppId, by }` (contrato 6.3), aos participantes
   * e a todo o staff da entidade. Idempotente por `chat`: repetir a mesma
   * atribuição (ou recebê-la depois do 201 do REST) não muda nada.
   */
  private onChatAssigned(data: any): void {
    if (!data || !data.chat || !data.userAppId) {
      return;
    }
    this.applyAssignment(String(data.chat), String(data.userAppId));
  }

  /**
   * Aplica um responsável à linha da lista, à conversa aberta e à fila. Uma
   * conversa (supervisionada ou não) que passa a ser do próprio fica editável;
   * uma supervisionada atribuída a outro muda de dono.
   */
  private applyAssignment(chat: string, userAppId: string, name?: string, at?: any): void {
    const assignedName = name || this.nameOf(userAppId);
    const mine = userAppId === this.meId;
    const build = (row: ChatUser): Partial<ChatUser> => {
      const patch: Partial<ChatUser> = {
        assignedUserAppId: userAppId,
        assignedName,
        assignedAt: at || row.assignedAt || new Date(),
        participants: this.upsertParticipant(row.participants, { id: userAppId, name: assignedName })
      };
      if (mine) {
        patch.supervised = false;
        patch.ownerUserId = userAppId;
        patch.ownerName = assignedName;
      } else if (row.supervised) {
        patch.ownerUserId = userAppId;
        patch.ownerName = assignedName;
      }
      return patch;
    };
    const entry = this.allUsers.find(u => !!u.chat && u.chat === chat);
    if (entry && (entry.assignedUserAppId !== userAppId || (mine && entry.supervised))) {
      this.replaceUser(entry, build(entry));
    }
    const conv = this.activeConversation;
    if (conv && conv.chat === chat && (conv.assignedUserAppId !== userAppId || (mine && conv.supervised))) {
      this.patchActive(build(conv));
    }
    // Assumida pelo próprio a partir da Supervisão ou da fila (2026-10-27): a lista
    // já não traz as conversas da equipa, por isso entra agora em "As minhas".
    const now = this.activeConversation;
    if (mine && !entry && now && now.chat === chat) {
      const { messages, ...row } = now;
      this.allUsers = [{ ...row, supervised: false, unreadCount: 0 }, ...this.allUsers];
      this.sortChats();
    }
    this.removeFromQueue(chat);
  }

  /** A mensagem de sistema `assigned` que o 201 do REST traz, no fio da conversa aberta (idempotente por messageId). */
  private absorbAssignSystemMessage(chat: string, result: ChatAssignResult): void {
    const conv = this.activeConversation;
    const sys = result.systemMessage;
    if (!conv || conv.chat !== chat || !sys) {
      return;
    }
    const message: ChatMessage = {
      messageId: sys.messageId,
      chat,
      kind: 'system',
      system: sys.system,
      message: '',
      time: sys.time,
      unread: false,
      files: [],
      userSender: { id: String(result.assignedBy || this.meId), name: this.nameOf(String(result.assignedBy || this.meId)) || '' },
      userReceiver: { id: String(result.assignedUserAppId), name: result.assignedName || '' }
    };
    this.absorbMessage(this.normalizeMessage(message));
  }

  private upsertParticipant(list: ChatParticipant[] | undefined, p: ChatParticipant): ChatParticipant[] {
    const rows = list || [];
    const id = String(p.id);
    if (rows.some(x => String(x.id) === id)) {
      return rows.map(x => String(x.id) === id ? { ...x, ...p, name: p.name || x.name, profile: p.profile || x.profile } : x);
    }
    return [...rows, p];
  }

  /* ---------------------------------------------------------------------
   * convidar consultor (invite_to_chat + chat_participant_added)
   * ------------------------------------------------------------------ */

  onInvite(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.capabilities.canInvite) {
      return;
    }
    this.inviteUserId = null;
    this.inviteOptions = this.computeInviteCandidates();
    // 2026-10-28: o diretório de colegas (ativos, email confirmado), sem quem já está ou foi convidado.
    this.openColleaguePicker([String(conv.userId || '')]
      .concat((conv.participants || []).map(p => String(p.id)))
      .concat(this.pendingInvites.map(p => String(p.userAppId))));
    this.openModal(this.inviteModal, 'md');
  }

  confirmInvite(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.inviteUserId) {
      return;
    }
    this.chatService.inviteToChat({ chat: conv.chat, userAppId: this.inviteUserId });
    this.closeModal();
  }

  /** `chat_participant_added { chat, participant }`: a mensagem de sistema `invited` chega a seguir por `receive_message`. */
  private onParticipantAdded(data: any): void {
    if (!data || !data.chat || !data.participant || !data.participant.id) {
      return;
    }
    const chat = String(data.chat);
    const p: ChatParticipant = { id: String(data.participant.id), name: data.participant.name, profile: data.participant.profile };
    if (p.id === this.meId) {
      this.removeInvite(chat);
    }
    this.pendingInvites = this.pendingInvites.filter(i => !(this.activeConversation && this.activeConversation.chat === chat && i.userAppId === p.id));
    const entry = this.allUsers.find(u => !!u.chat && u.chat === chat);
    if (entry) {
      this.replaceUser(entry, { participants: this.upsertParticipant(entry.participants, p) });
    } else if (p.id === this.meId) {
      // Fui eu o convidado: a conversa passa a ser minha e ainda não está na lista.
      this.initChat();
    }
    const conv = this.activeConversation;
    if (conv && conv.chat === chat) {
      this.patchActive({ participants: this.upsertParticipant(conv.participants, p) });
    }
  }

  /* ---------------------------------------------------------------------
   * convites por aceitar (contrato 6.3, v8)
   * ------------------------------------------------------------------ */

  /** `chat_invite`: fui convidado. Fica no topo da lista até responder. */
  private onInviteReceived(data: ChatInvite): void {
    if (!data || !data.chat) {
      return;
    }
    this.invites = [data, ...this.invites.filter(i => i.chat !== data.chat)];
  }

  /** `chat_invite_sent`: alguém desta conversa foi convidado. A quem convidou, a confirmação. */
  private onInviteSent(data: { chat: string; userAppId: string; name?: string; profile?: string; invitedAt: string; by: string }): void {
    if (!data || !data.chat) {
      return;
    }
    if (data.by === this.meId) {
      this.toast(this.strings.inviteSent.replace('{name}', data.name || ''));
    }
    const conv = this.activeConversation;
    if (conv && conv.chat === data.chat) {
      const row: ChatPendingInvite = { userAppId: data.userAppId, name: data.name, profile: data.profile, invitedAt: data.invitedAt, by: data.by };
      this.pendingInvites = [...this.pendingInvites.filter(i => i.userAppId !== data.userAppId), row];
    }
  }

  /** `chat_invite_answered` (recusa): ao staff da conversa e ao próprio (noutro separador). */
  private onInviteAnswered(data: { chat: string; userAppId: string; name?: string; accepted: boolean }): void {
    if (!data || !data.chat) {
      return;
    }
    if (data.userAppId === this.meId) {
      this.removeInvite(data.chat);
      return;
    }
    if (!data.accepted) {
      this.errorMsg(this.strings.inviteDeclined.replace('{name}', data.name || ''));
    }
    this.pendingInvites = this.pendingInvites.filter(i => i.userAppId !== data.userAppId);
  }

  /** `chat_invite_cancelled`: o convite saiu (o meu, ou o de um colega nesta conversa). */
  private onInviteCancelled(data: { chat: string; userAppId: string }): void {
    if (!data || !data.chat) {
      return;
    }
    if (data.userAppId === this.meId) {
      this.removeInvite(data.chat);
      return;
    }
    const conv = this.activeConversation;
    if (conv && conv.chat === data.chat) {
      this.pendingInvites = this.pendingInvites.filter(i => i.userAppId !== data.userAppId);
    }
  }

  /** Aceitar: entro na conversa (chega `chat_participant_added`) e ela abre quando estiver na lista. */
  acceptInvite(chat: string): void {
    if (this.inviteBusy) {
      return;
    }
    this.inviteBusy = chat;
    this.chatService.answerInvite(chat, true).subscribe(ack => {
      this.inviteBusy = null;
      if (ack && ack.ok) {
        this.removeInvite(chat);
        this.pendingChatParam = chat;
        this.initChat();
      } else {
        this.loadInvites();
      }
    });
  }

  declineInvite(chat: string): void {
    if (this.inviteBusy) {
      return;
    }
    this.inviteBusy = chat;
    this.chatService.answerInvite(chat, false).subscribe(ack => {
      this.inviteBusy = null;
      if (ack && ack.ok) {
        this.removeInvite(chat);
      } else {
        this.loadInvites();
      }
    });
  }

  /** "Cancelar convite" no painel de detalhes. */
  cancelInvite(userAppId: string): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !userAppId) {
      return;
    }
    const chat = conv.chat;
    this.chatService.cancelInvite(chat, userAppId).subscribe(ack => {
      if (ack && ack.ok && this.activeConversation && this.activeConversation.chat === chat) {
        this.pendingInvites = this.pendingInvites.filter(i => i.userAppId !== userAppId);
      }
    });
  }

  private removeInvite(chat: string): void {
    if (this.invites.some(i => i.chat === chat)) {
      this.invites = this.invites.filter(i => i.chat !== chat);
    }
  }

  /** `GET chat/:chat/pending-invites`: só para quem pode ver a conversa (o back-end volta a verificar). */
  private loadPendingInvites(conv: ChatConversation | null): void {
    this.pendingInvites = [];
    if (!conv || !conv.chat) {
      return;
    }
    const chat = conv.chat;
    this.subscriptions.add(this.chatService.getPendingInvites(chat).subscribe(list => {
      if (this.activeConversation && this.activeConversation.chat === chat) {
        this.pendingInvites = list || [];
      }
    }));
  }

  /* ---------------------------------------------------------------------
   * presença própria (set_presence) e "a escrever…" (typing / chat_typing)
   * ------------------------------------------------------------------ */

  onSetPresence(status: 'online' | 'away'): void {
    if (!this.capabilities.canSetAway) {
      return;
    }
    this.chatService.setPresence(status);
  }

  /**
   * O servidor confirma (ou repõe, ao religar) a presença do próprio por
   * `chat_presence`; só se reage a uma MUDANÇA do valor do próprio, para uma
   * emissão sobre outro utilizador não apagar um `away` acabado de pedir.
   */
  private syncMyPresence(): void {
    const me = this.meId;
    if (!me) {
      return;
    }
    const mine = this.presence[me];
    if (mine === this.mySeenPresence) {
      return;
    }
    this.mySeenPresence = mine;
    if ((mine === 'online' || mine === 'away') && mine !== this.chatService.myPresence$.value) {
      this.chatService.myPresence$.next(mine);
    }
  }

  /** O compositor já limita a cadência (início / fim, 3 s); aqui só se endereça a conversa. */
  onTyping(typing: boolean): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || conv.supervised) {
      return;
    }
    this.chatService.sendTyping({ chat: conv.chat, typing });
  }

  /** A gravar uma mensagem de voz: o outro lado vê "a gravar áudio…" (o mesmo canal do "a escrever…"). */
  onRecording(active: boolean): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || conv.supervised) {
      return;
    }
    this.chatService.sendTyping({ chat: conv.chat, typing: active, mode: 'voice' });
  }

  private onChatTyping(data: any): void {
    const conv = this.activeConversation;
    if (!data || !conv || !conv.chat || conv.chat !== data.chat || String(data.userAppId) === this.meId) {
      return;
    }
    this.clearTypingTimer();
    this.typingActive = !!data.typing;
    this.typingMode = data.mode === 'voice' ? 'voice' : 'text';
    if (this.typingActive) {
      this.typingTimer = setTimeout(() => {
        this.typingTimer = null;
        this.typingActive = false;
      }, TYPING_EXPIRE_MS);
    }
  }

  private clearTypingTimer(): void {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
  }

  /* ---------------------------------------------------------------------
   * enviar imóvel (modal + send_property)
   * ------------------------------------------------------------------ */

  onSuggestProperty(filters: any = null): void {
    const conv = this.activeConversation;
    if (!conv || !this.capabilities.canSuggestProperties || conv.supervised) {
      return;
    }
    this.propertyFilters = filters;
    this.propertyPickMode = 'send';
    this.propertyPreselectIds = this.propertyPreselect || [];
    // "Ver e enviar" já escolheu o âmbito; o botão normal abre na agência.
    this.propertyPickerScope = this.propertyPreselect ? this.widestPropertyScope() : 'store';
    this.propertyPreselect = null;
    this.propertySelected = [];
    this.openModal(this.propertyModal, 'lg');
  }

  /**
   * O âmbito mais largo que este utilizador vê: toda a rede com
   * VIEW_PROPERTIES_NETWORK, senão a agência (para o MASTER "agência" já é a
   * entidade). É o âmbito em que se contam os imóveis que encaixam na
   * qualificação e em que o "Ver e enviar" abre o seletor: antes a contagem
   * ia sem âmbito (só os do consultor) e o seletor abria na agência, e um
   * consultor via a barra dizer "3 encaixam" e o seletor vazio.
   */
  private widestPropertyScope(): ChatPropertyScope {
    return this.canSeeNetworkProperties ? 'network' : 'store';
  }

  /** A flag de âmbito de `POST /properties/all` para `widestPropertyScope()` (o MASTER não leva nenhuma). */
  private widestPropertyScopeBody(): any {
    if (this.isMasterUser) {
      return {};
    }
    return this.widestPropertyScope() === 'network' ? { showNetworkProperties: true } : { showStoreProperties: true };
  }

  /** Base das fotos dos imóveis, para o seletor. */
  get propertyImageBase(): string {
    return this.pathPropertyImage;
  }

  /** "Toda a rede" no seletor: a permissão da lista de imóveis (o MASTER já vê a entidade). */
  get canSeeNetworkProperties(): boolean {
    return !this.isMasterUser && this.permissionsService.hasPermission('VIEW_PROPERTIES_NETWORK');
  }

  get isMasterUser(): boolean {
    return this.permissionsService.isRealMaster(this.userLogged && this.userLogged.role ? this.userLogged.role.name : null);
  }

  /* ---- o imóvel de uma visita / horários / proposta: dentro do modal, com "trocar" ---- */

  /** O imóvel da conversa no formato do seletor, ou null. */
  private conversationProperty(): ChatPropertyPick | null {
    const conv = this.activeConversation;
    if (!conv || !conv.propertyId) {
      return null;
    }
    const file = conv.propertyImage ? String(conv.propertyImage) : '';
    return {
      id: conv.propertyId, reference: conv.propertyReference, title: conv.propertyTitle,
      image: file ? (this.isResolvedUrl(file) ? file : this.pathPropertyImage + file) : undefined
    };
  }

  /** "Trocar" no modal de visita / horários (ou sem imóvel): o seletor em modo "um só", e volta ao mesmo modal. */
  changeVisitProperty(returnTo: 'visit' | 'slots' | 'proposal'): void {
    this.propertyReturn = returnTo;
    this.propertyPickMode = 'confirm';
    // Os filtros de "Procurar imóveis" (qualificação) eram de outro pedido.
    this.propertyFilters = null;
    this.propertyPreselectIds = [];
    this.propertyPickerScope = 'store';
    this.propertySelected = [];
    this.openModal(this.propertyModal, 'lg');
  }

  /** Cancelar o seletor em modo "escolher um" volta ao modal de onde veio; em modo "enviar" fecha. */
  cancelPropertyPick(): void {
    if (this.propertyPickMode === 'confirm') {
      this.propertyPickMode = 'send';
      this.propertySelected = [];
      this.returnFromPropertyPick();
      return;
    }
    this.closeModal();
  }

  /** Reabre o modal que pediu o imóvel, com o estado que já lá estava (ou nada, se a proposta não tem imóvel). */
  private returnFromPropertyPick(): void {
    const target = this.propertyReturn;
    this.propertyReturn = null;
    if (target === 'visit' || target === 'slots') {
      // O modal único guarda o modo ("agora" / "perguntar") e o resto do estado.
      this.openModal(this.visitModal, 'md');
    } else {
      this.closeModal();
    }
  }


  /** Um `send_property` por imóvel, cada um com o seu cartão otimista (`kind: 'property'`). */
  confirmSendProperties(): void {
    if (this.propertyPickMode === 'confirm') {
      const pick = this.propertySelected[0];
      this.propertyPickMode = 'send';
      this.propertySelected = [];
      if (pick) {
        this.visitProperty = pick;
      }
      if (this.propertyReturn === 'proposal') {
        this.propertyReturn = null;
        this.closeModal();
        if (pick) {
          this.startProposal(pick);
        }
        return;
      }
      this.returnFromPropertyPick();
      return;
    }
    const conv = this.activeConversation;
    if (!conv || this.propertySelected.length === 0) {
      return;
    }
    const picks = this.propertySelected.slice();
    this.closeModal();
    picks.forEach(pick => this.sendPropertyCard(pick));
  }

  private sendPropertyCard(pick: ChatPropertyPick): void {
    const conv = this.activeConversation;
    if (!conv) {
      return;
    }
    const pending: ChatMessage = {
      idTemp: Date.now() + '-' + String(pick.id),
      clientMessageId: this.nextClientId(),
      kind: 'property',
      message: '',
      time: new Date(),
      unread: true,
      chat: conv.chat,
      files: [],
      sendState: 'sending',
      property: this.withPropertyRoute({
        id: pick.id, reference: pick.reference, title: pick.title, price: this.priceText(pick), image: pick.image
      }),
      userSender: { id: String(this.meId), name: String(this.userLogged.name || '') },
      userReceiver: { id: String(conv.userId), name: conv.name, profile: conv.photo }
    };
    this.patchActive({ messages: [...conv.messages, pending] });
    this.armSendTimer(pending);
    this.emitSend(pending, conv);
  }

  /** Uma linha de `POST /properties/all/:lang` (já mapeada pelo PropertiesService) no formato do seletor. */
  private toPropertyPick(p: any): ChatPropertyPick {
    const preview = p && p.preview ? p.preview : {};
    const location = [preview.county, preview.district].filter((x: any) => !!x).join(' · ');
    return {
      id: p.id,
      reference: p.reference,
      title: p.title || preview.title,
      price: p.price,
      currency: p.currency,
      image: p.image ? this.pathPropertyImage + p.image : undefined,
      location: location || undefined
    };
  }

  priceText(p: ChatPropertyPick): string {
    if (p.price === undefined || p.price === null || p.price === '') {
      return '';
    }
    const n = Number(p.price);
    if (isNaN(n)) {
      return String(p.price);
    }
    return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ' + (p.currency || '€');
  }

  /* ---------------------------------------------------------------------
   * nova conversa entre colegas ("+" no segmento Consultores)
   * ------------------------------------------------------------------ */

  openNewConversation(): void {
    this.newConversationId = null;
    this.openColleaguePicker([]);
    this.openModal(this.newConversationModal, 'md');
  }

  /** Abre a conversa (privada, a dois) com o colega escolhido: a linha da lista se já existir, senão uma nova. */
  startConversation(): void {
    const id = this.newConversationId ? String(this.newConversationId) : '';
    const colleague = id ? this.colleagues.users.find(u => u.userAppId === id) : undefined;
    if (!colleague) {
      return;
    }
    this.closeModal();
    const listed = this.allUsers.find(u => u.userId === id && isStaffRow(u) && !u.supervised && !u.propertyId && !u.propertyReference);
    this.getConversation(listed || this.colleagueAsUser(colleague));
  }

  /** Um colega do diretório como linha da lista (conversa privada, ainda sem id). */
  private colleagueAsUser(c: ChatColleague): ChatUser {
    return {
      userId: c.userAppId, name: c.name, image: c.photo || undefined, role: c.role || 'AGENT', status: '', unreadCount: 0
    } as ChatUser;
  }

  /* ---- o seletor de colegas (2026-10-28): loja por omissão, rede com filtro por agência; MASTER sempre rede ---- */

  private openColleaguePicker(exclude: string[]): void {
    this.colleagueExclude = exclude;
    this.colleagueScope = this.isMasterUser ? 'network' : 'store';
    this.colleagueStoreId = null;
    this.colleagueSearch = '';
    this.colleagues = { scope: this.colleagueScope, master: this.isMasterUser, stores: [], users: [] };
    this.loadColleagues();
  }

  private loadColleagues(): void {
    const seq = ++this.colleaguesSeq;
    this.colleaguesLoading = true;
    this.subscriptions.add(this.chatService.getColleagues({ scope: this.colleagueScope, storeId: this.colleagueStoreId, search: this.colleagueSearch })
      .pipe(catchError(() => of({ scope: this.colleagueScope, master: this.isMasterUser, stores: [], users: [] } as ChatColleaguesPage)))
      .subscribe(page => {
        if (seq !== this.colleaguesSeq) {
          return;
        }
        this.colleaguesLoading = false;
        this.colleagues = page;
        this.colleagueScope = page.scope;
      }));
  }

  onColleagueScope(scope: ChatColleagueScope): void {
    this.colleagueScope = scope;
    this.colleagueStoreId = null;
    this.loadColleagues();
  }

  onColleagueStore(storeId: number | null): void {
    this.colleagueStoreId = storeId;
    this.loadColleagues();
  }

  onColleagueSearch(search: string): void {
    this.colleagueSearch = search;
    this.loadColleagues();
  }

  /** As menções usam a rede inteira (lida uma vez por sessão). */
  private loadMentionPool(): void {
    if (this.colleagueMentionPool !== null) {
      return;
    }
    this.colleagueMentionPool = [];
    this.subscriptions.add(this.chatService.getColleagues({ scope: 'network' }).pipe(catchError(() => of(null))).subscribe(page => {
      this.colleagueMentionPool = page ? page.users : [];
    }));
  }

  /* ---------------------------------------------------------------------
   * visitas (7.3): agendar, pedir disponibilidade, cancelar, ficha virtual
   * ------------------------------------------------------------------ */

  /**
   * "Agendar visita": tudo num modal — o imóvel (da conversa, com "trocar"), atalhos
   * de data, data/hora, duração, aviso de agenda e o email ao cliente. `prefill` vem
   * de um cartão de imóvel (o imóvel desse cartão).
   */
  openScheduleVisit(prefill?: VisitPrefill): void {
    const conv = this.activeConversation;
    if (!conv || !this.canManageVisitsHere) {
      return;
    }
    this.prepareVisit(prefill);
    this.openModal(this.visitModal, 'md');
    this.onVisitWhenChange();
    this.loadAgendaPicks();
  }

  /**
   * Secção 11: os atalhos passam a ser horários LIVRES da agenda do consultor da
   * conversa (`GET /chat/:chat/visit-slots`); até chegarem, ficam os calculados.
   */
  private loadAgendaPicks(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat) {
      return;
    }
    const seq = ++this.agendaPicksSeq;
    this.subscriptions.add(this.chatService.getVisitSlots(conv.chat, { days: 7, durationMinutes: this.visitDuration }).subscribe(res => {
      if (seq !== this.agendaPicksSeq || !res || res.slots.length === 0) {
        return;
      }
      const now = new Date();
      const picks = res.slots.map(iso => quickPickFromIso(iso, now, this.strings)).filter((p): p is ChatQuickPick => !!p);
      // Variedade: no máximo dois por dia, até seis.
      const perDay: { [date: string]: number } = {};
      this.visitQuickPicks = picks.filter(p => {
        perDay[p.date] = (perDay[p.date] || 0) + 1;
        return perDay[p.date] <= 2;
      }).slice(0, 6);
    }));
  }

  /** O estado do modal de marcar, sem o abrir (o seletor de imóvel reabre-o depois com este estado). */
  private prepareVisit(prefill?: VisitPrefill): void {
    const conv = this.activeConversation;
    this.visitReschedule = prefill && prefill.reschedule ? prefill.reschedule : null;
    this.visitMode = !this.visitReschedule && prefill && prefill.mode === 'ask' ? 'ask' : 'now';
    this.visitProperty = (prefill && prefill.property) || this.conversationProperty();
    this.visitQuickPicks = buildQuickPicks(new Date(), this.strings);
    const start = prefill && prefill.startAt ? new Date(prefill.startAt) : (this.visitQuickPicks[0] ? this.visitQuickPicks[0].at : nextRoundHour());
    this.visitDate = toDateInput(start);
    this.visitTime = toTimeInput(start);
    this.visitDuration = (prefill && prefill.durationMinutes) || 60;
    this.visitSendEmail = !!(conv && conv.client && conv.client.email);
    this.visitConflicts = [];
    this.visitBusy = false;
    this.visitRequestMessageId = prefill && prefill.requestMessageId ? prefill.requestMessageId : null;
    // "Perguntar disponibilidade": dois horários sugeridos para começar, e a nota vazia.
    const first = this.visitQuickPicks.slice(0, 2);
    this.visitSlots = first.length > 0
      ? first.map(p => ({ date: p.date, time: p.time }))
      : [{ date: toDateInput(nextRoundHour(24)), time: toTimeInput(nextRoundHour(24)) }];
    this.visitNote = '';
  }

  /** Troca entre "Agendar agora" e "Perguntar disponibilidade" (o resto do estado fica). */
  setVisitMode(mode: 'now' | 'ask'): void {
    this.visitMode = mode;
    if (mode === 'now') {
      this.onVisitWhenChange();
    }
  }

  /**
   * "Perguntar disponibilidade" (antes "Propor horários"): o mesmo modal de
   * "Agendar visita", já nesse modo. Até 5 horários; o cliente escolhe na conversa
   * e a visita fica agendada sozinha.
   */
  openProposeSlots(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.canManageVisitsHere) {
      return;
    }
    this.openScheduleVisit({ mode: 'ask' });
  }

  /** Atalho de data no modal de marcar. */
  applyVisitPick(p: ChatQuickPick): void {
    this.visitDate = p.date;
    this.visitTime = p.time;
    this.onVisitWhenChange();
  }

  isVisitPickOn(p: ChatQuickPick): boolean {
    return this.visitDate === p.date && this.visitTime === p.time;
  }

  /** Atalho no modal de horários: liga/desliga o horário na lista (máx. 5). */
  toggleSlotPick(p: ChatQuickPick): void {
    const idx = this.visitSlots.findIndex(s => s.date === p.date && s.time === p.time);
    if (idx > -1) {
      this.visitSlots = this.visitSlots.filter((_s, i) => i !== idx);
      return;
    }
    if (this.visitSlots.length >= 5) {
      return;
    }
    this.visitSlots = [...this.visitSlots, { date: p.date, time: p.time }];
  }

  isSlotPickOn(p: ChatQuickPick): boolean {
    return this.visitSlots.some(s => s.date === p.date && s.time === p.time);
  }

  /** Aviso de sobreposição (nunca bloqueia), com um pequeno atraso para não bater no servidor a cada tecla. */
  onVisitWhenChange(): void {
    if (this.availabilityTimer) {
      clearTimeout(this.availabilityTimer);
    }
    this.availabilityTimer = setTimeout(() => {
      this.availabilityTimer = null;
      const when = combineDateTime(this.visitDate, this.visitTime);
      if (!when) {
        this.visitConflicts = [];
        return;
      }
      this.subscriptions.add(this.chatService.checkVisitAvailability(when.toISOString(), this.visitDuration).subscribe(res => {
        const list: any[] = res && Array.isArray(res.conflicts) ? res.conflicts : (Array.isArray(res) ? res : []);
        this.visitConflicts = list.map(c => ({
          name: String((c && (c.name || c.title)) || this.strings.visitCardTitle),
          from: formatTime(c && (c.startAt || c.start)),
          to: formatTime(c && (c.endAt || c.end))
        }));
      }));
    }, 350);
  }

  /** "Sobrepõe-se a: Visita Rua X, 10:00–11:00; …" */
  get visitConflictText(): string {
    if (this.visitConflicts.length === 0) {
      return '';
    }
    const items = this.visitConflicts.map(c => c.name + (c.from ? ', ' + c.from + (c.to ? '–' + c.to : '') : '')).join('; ');
    return this.strings.conflictWith.replace('{items}', items);
  }

  addVisitSlot(): void {
    if (this.visitSlots.length >= 5) {
      return;
    }
    const last = this.visitSlots[this.visitSlots.length - 1];
    const base = last ? combineDateTime(last.date, last.time) : null;
    const next = base ? new Date(base.getTime() + 24 * 3600000) : nextRoundHour(24);
    this.visitSlots = [...this.visitSlots, { date: toDateInput(next), time: toTimeInput(next) }];
  }

  removeVisitSlot(i: number): void {
    if (this.visitSlots.length <= 1) {
      return;
    }
    this.visitSlots = this.visitSlots.filter((_s, idx) => idx !== i);
  }

  get hasValidSlots(): boolean {
    return this.visitSlots.length > 0 && this.visitSlots.every(s => !!combineDateTime(s.date, s.time));
  }

  /** "Marcar visita": REST no back-end (cria lead/negócio, visita, calendário, email) e anúncio ao rtn. */
  submitVisitNow(): void {
    const conv = this.activeConversation;
    const when = combineDateTime(this.visitDate, this.visitTime);
    if (!conv || !conv.chat || !when || this.visitBusy) {
      return;
    }
    const chat = conv.chat;
    if (this.visitReschedule) {
      // Secção 11: só o horário muda; o imóvel e o pedido ficam.
      const visitId = this.visitReschedule.visitId;
      this.visitBusy = true;
      this.subscriptions.add(this.chatService.rescheduleChatVisit(chat, visitId, { startAt: when.toISOString(), durationMinutes: this.visitDuration }).subscribe({
        next: result => {
          this.visitBusy = false;
          this.closeModal();
          const ids: string[] = (result && result.messageIds) || [];
          const patch = { startAt: result.startAt, durationMinutes: result.durationMinutes, status: 'SCHEDULED', rescheduledAt: new Date().toISOString() };
          this.mapMessages(m => m.messageId && ids.indexOf(m.messageId) > -1
            ? { ...m, params: { ...(m.params || {}), previousStartAt: (m.params || {})['startAt'], ...patch } }
            : m);
          ids.forEach(id => this.chatService.announceMessage({ messageId: id, update: true }));
          this.loadDealContext(this.activeConversation);
          this.toast(this.strings.visitRescheduledToast);
        },
        error: err => {
          this.visitBusy = false;
          this.errorMsg(this.apiError.resolve(err, this.strings.visitFailed).message);
        }
      }));
      return;
    }
    if (!this.visitProperty) {
      this.changeVisitProperty('visit');
      return;
    }
    this.visitBusy = true;
    this.subscriptions.add(this.chatService.createChatVisit(chat, {
      startAt: when.toISOString(),
      durationMinutes: this.visitDuration,
      propertyId: String(this.visitProperty.id),
      requestMessageId: this.visitRequestMessageId || undefined,
      sendClientEmail: this.visitSendEmail
    }).subscribe({
      next: result => {
        this.visitBusy = false;
        this.closeModal();
        this.afterVisitScheduled(chat, result);
      },
      error: err => {
        this.visitBusy = false;
        this.errorMsg(this.apiError.resolve(err, this.strings.visitFailed).message);
      }
    }));
  }

  /** Depois de o back-end marcar: anuncia o cartão, fecha o pedido (se houver) e atualiza lead/negócio. */
  private afterVisitScheduled(chat: string, result: any): void {
    if (result && result.messageId) {
      this.chatService.announceMessage({ messageId: result.messageId });
    }
    if (result && result.requestMessageId) {
      this.mapMessages(m => m.messageId === result.requestMessageId
        ? { ...m, params: { ...(m.params || {}), status: 'scheduled', visitId: result.visit && result.visit.id, visitMessageId: result.messageId } }
        : m);
      // O pedido fechou no back-end: os outros participantes recebem o estado novo.
      this.chatService.announceMessage({ messageId: result.requestMessageId, update: true });
    }
    this.refreshConversationLead(chat);
    this.loadDealContext(this.activeConversation);
    this.toast(this.strings.visitScheduledToast);
  }

  /**
   * Um clique no cartão ("Confirmar visita" / "Aceitar"): o horário que o cliente
   * escolheu ou propôs, a duração do pedido e o imóvel da conversa — sem modais.
   * Só abre o seletor se a conversa não tiver imóvel.
   */
  private scheduleFromRequest(m: ChatMessage, when: string): void {
    const conv = this.activeConversation;
    const params: any = m.params || {};
    if (!conv || !conv.chat || !m.messageId || !when || this.visitActionBusy) {
      return;
    }
    const duration = Number(params.durationMinutes) || 60;
    const property = this.conversationProperty();
    if (!property) {
      this.prepareVisit({ startAt: when, requestMessageId: m.messageId, durationMinutes: duration });
      this.changeVisitProperty('visit');
      return;
    }
    const chat = conv.chat;
    this.visitActionBusy = m.messageId;
    this.subscriptions.add(this.chatService.createChatVisit(chat, {
      startAt: when, durationMinutes: duration, propertyId: String(property.id), requestMessageId: m.messageId, sendClientEmail: true
    }).subscribe({
      next: result => {
        this.visitActionBusy = null;
        this.afterVisitScheduled(chat, result);
      },
      error: err => {
        this.visitActionBusy = null;
        this.errorMsg(this.apiError.resolve(err, this.strings.visitFailed).message);
      }
    }));
  }

  /** "Enviar horários": vai pelo socket (send_visit_request); o cartão chega por receive_message. */
  submitVisitRequest(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.hasValidSlots || this.visitBusy) {
      return;
    }
    const slots = this.visitSlots.map(s => combineDateTime(s.date, s.time)).filter((d): d is Date => !!d).map(d => d.toISOString());
    this.chatService.sendVisitRequest({
      chat: conv.chat, slots, durationMinutes: this.visitDuration, note: this.visitNote.trim() || undefined,
      propertyId: this.visitProperty ? this.visitProperty.id : undefined
    });
    this.closeModal();
    this.expectToast('visit_request', this.strings.requestSentToast);
  }

  /** A lead/negócio podem ter nascido com a visita: recarrega a linha da lista (painel de detalhes). */
  private refreshConversationLead(chat: string): void {
    this.subscriptions.add(this.chatService.getAllUsersChat().subscribe((list: any[]) => {
      const fresh = (list || []).find((u: any) => u && u.chat === chat);
      if (!fresh) {
        return;
      }
      const entry = this.allUsers.find(u => u.chat === chat);
      if (entry) {
        this.replaceUser(entry, { client: fresh.client, lead: fresh.lead, deal: fresh.deal, lastMessageKind: fresh.lastMessageKind });
      }
      const conv = this.activeConversation;
      if (conv && conv.chat === chat) {
        this.patchActive({ client: fresh.client, lead: fresh.lead, deal: fresh.deal });
      }
    }));
  }

  /** Ações dos cartões de visita (fio → contentor). */
  onVisitAction(action: ChatVisitAction): void {
    const conv = this.activeConversation;
    const m = action.message;
    if (!conv || !conv.chat || !m || !m.messageId) {
      return;
    }
    const params: any = m.params || {};
    switch (action.type) {
      case 'choose':
        this.chatService.answerVisitRequest({ chat: conv.chat, messageId: m.messageId, chosen: action.when });
        break;
      case 'propose':
        this.chatService.answerVisitRequest({ chat: conv.chat, messageId: m.messageId, proposed: action.when });
        break;
      case 'decline':
        this.chatService.answerVisitRequest({ chat: conv.chat, messageId: m.messageId, declined: true });
        break;
      case 'schedule':
        if (m.kind === 'property') {
          const p = m.property;
          this.openScheduleVisit({ property: p ? { id: p.id, reference: p.reference, title: p.title, price: p.price, image: p.image } : null });
        } else if (action.when) {
          // Um clique: o horário do cartão, a duração do pedido e o imóvel da conversa.
          this.scheduleFromRequest(m, action.when);
        }
        break;
      case 'proposeOther':
        // O consultor propõe horários novos (um pedido novo); o antigo fica como está.
        this.openProposeSlots();
        break;
      case 'reschedule':
        if (params.visitId) {
          this.openScheduleVisit({ startAt: params.startAt, durationMinutes: Number(params.durationMinutes) || undefined, reschedule: { visitId: Number(params.visitId) } });
        }
        break;
      case 'cancel':
        this.cancelVisit(m);
        break;
      case 'ics':
        this.downloadIcs(conv, params);
        break;
      case 'requestForm':
        if (params.visitId) {
          this.sendVisitForm({ id: Number(params.visitId), startAt: params.startAt, status: params.status || 'SCHEDULED' });
        }
        break;
      case 'copyForm':
        this.copyToClipboard(params.url);
        break;
    }
  }

  /** Cancelar pede o motivo (obrigatório, como no calendário) e anuncia o cartão atualizado. */
  private cancelVisit(m: ChatMessage): void {
    const conv = this.activeConversation;
    const params: any = m.params || {};
    if (!conv || !conv.chat || !params.visitId) {
      return;
    }
    const chat = conv.chat;
    Swal.fire({
      title: this.strings.visitCancel,
      input: 'textarea',
      inputLabel: this.strings.visitCancelReason,
      inputAttributes: { maxlength: '500' },
      inputValidator: (value: string) => (!value || !value.trim() ? this.strings.visitCancelReason : null),
      showCancelButton: true,
      confirmButtonText: this.strings.visitCancelConfirm,
      cancelButtonText: this.strings.cancel,
      confirmButtonColor: '#f06548'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }
      this.subscriptions.add(this.chatService.cancelChatVisit(chat, Number(params.visitId), String(result.value).trim()).subscribe({
        next: res => {
          const ids: string[] = (res && res.messageIds) || [];
          this.mapMessages(x => x.messageId && ids.indexOf(x.messageId) > -1
            ? { ...x, params: { ...(x.params || {}), status: 'CANCELED', cancelReason: String(result.value).trim() } }
            : x);
          // Cartões que já existem: os outros recebem só o estado novo (receive_message_update).
          ids.forEach(id => this.chatService.announceMessage({ messageId: id, update: true }));
          this.loadDealContext(this.activeConversation);
          this.toast(this.strings.visitCanceledToast);
        },
        error: err => this.errorMsg(this.apiError.resolve(err, this.strings.visitFailed).message)
      }));
    });
  }

  /** .ics de uma visita, gerado no cliente (sem servidor). */
  private downloadIcs(conv: ChatConversation, params: any): void {
    if (!params.startAt) {
      return;
    }
    const title = this.strings.visitCardTitle + (conv.propertyReference ? ' · ' + conv.propertyReference : '') + (conv.name ? ' · ' + conv.name : '');
    const ics = buildIcs({
      uid: 'imodigi-visit-' + params.visitId + '@chat',
      title,
      description: conv.propertyTitle || undefined,
      start: new Date(params.startAt),
      durationMinutes: Number(params.durationMinutes) || 60
    });
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'visita-' + (params.visitId || 'imodigi') + '.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /** "Pedir avaliação": lista as visitas da conversa e envia a ficha virtual como cartão. */
  openRequestVisitForm(): void {
    const conv = this.activeConversation;
    if (!conv || !this.canManageVisitsHere) {
      return;
    }
    const chat = conv.chat!;
    this.visitList = [];
    this.visitListLoading = true;
    this.visitFormBusy = null;
    this.openModal(this.visitFormModal, 'md');
    this.subscriptions.add(this.chatService.getChatVisits(chat).subscribe({
      next: list => { this.visitList = list.filter(v => this.isVisitFormable(v)); this.visitListLoading = false; },
      error: () => { this.visitListLoading = false; }
    }));
  }

  sendVisitForm(v: { id: number; startAt?: string; status?: string }): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || this.visitFormBusy) {
      return;
    }
    this.visitFormBusy = v.id;
    this.subscriptions.add(this.chatService.issueVirtualVisitForm(v.id, conv.chat).subscribe({
      next: res => {
        this.visitFormBusy = null;
        this.closeModal();
        if (res && res.messageId) {
          this.chatService.announceMessage({ messageId: res.messageId });
        }
        this.toast(this.strings.formSentToast);
      },
      error: err => {
        this.visitFormBusy = null;
        this.errorMsg(this.apiError.resolve(err, this.strings.formFailed).message);
      }
    }));
  }

  visitWhen(v: ChatVisitRow): string {
    return formatWhen(v.startAt);
  }

  trackByVisit = (_i: number, v: ChatVisitRow) => v.id;

  private copyToClipboard(text: string | undefined): void {
    if (!text) {
      return;
    }
    const done = () => this.toast(this.strings.linkCopied);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, () => this.notice(this.strings.visitFormCopyLink, text));
    } else {
      this.notice(this.strings.visitFormCopyLink, text);
    }
  }

  /** Feedback curto com o toastr do CRM (o forRoot é "fixo": aqui dá-se um tempo). */
  private toast(message: string): void {
    this.toastr.success(message, undefined, { timeOut: 2500, extendedTimeOut: 1000, disableTimeOut: false, closeButton: true, progressBar: true });
  }

  /* ---------------------------------------------------------------------
   * fase 3: qualificação, proposta, negócio, lembretes, transferência, menções
   * ------------------------------------------------------------------ */

  /** Colegas (staff) que se podem mencionar numa nota (canMention, só em conversas de cliente). */
  get mentionCandidates(): ChatMention[] {
    if (!this.activeIsClient || !this.capabilities.canMention) {
      return [];
    }
    // 2026-10-28: o diretório (ativos, email confirmado); até chegar, as linhas da lista.
    this.loadMentionPool();
    if (this.colleagueMentionPool && this.colleagueMentionPool.length > 0) {
      return this.colleagueMentionPool.filter(u => u.userAppId !== this.meId).map(u => ({ id: u.userAppId, name: u.name }));
    }
    return this.dedupeByUser(this.allUsers.filter(u => !!u.userId && isStaffRow(u) && !u.supervised && u.userId !== this.meId))
      .map(u => ({ id: String(u.userId), name: u.name }));
  }

  /** Ações do menu "+" do compositor: só as que não têm ícone próprio, e só as que a conversa permite. */
  get composerActions(): ChatComposerAction[] {
    if (!this.activeIsClient) {
      return [];
    }
    const out: ChatComposerAction[] = [];
    if (this.canManageVisitsHere) {
      out.push({ key: 'visit', label: this.strings.scheduleVisit, icon: 'clock' });
      if (this.hasVisitForForm) {
        out.push({ key: 'visitForm', label: this.strings.visitRequestForm, icon: 'file' });
      }
    }
    if (this.canQualifyHere) {
      out.push({ key: 'qualify', label: this.strings.qualify, icon: 'help' });
    }
    if (this.canManageVisitsHere) {
      out.push({ key: 'proposal', label: this.strings.sendProposal, icon: 'file' });
    }
    return out;
  }

  onComposerAction(key: string): void {
    switch (key) {
      case 'visit': this.openScheduleVisit(); break;
      case 'slots': this.openProposeSlots(); break;
      case 'visitForm': this.openRequestVisitForm(); break;
      case 'qualify': this.openQualification(); break;
      case 'proposal': this.startProposal(); break;
    }
  }

  /** Retirar colegas: o responsável da conversa, ou quem transfere (MASTER / TRANSFER_CHAT). Só com cliente. */
  get canRemoveParticipantsHere(): boolean {
    const conv = this.activeConversation;
    if (!conv || !this.activeIsClient) {
      return false;
    }
    return !!this.capabilities.canTransfer || (!!conv.assignedUserAppId && String(conv.assignedUserAppId) === this.meId);
  }

  /** Sair: participo (convidado ou mencionado), não sou o responsável, e é uma conversa com cliente. */
  get canLeaveHere(): boolean {
    const conv = this.activeConversation;
    if (!conv || !this.activeIsClient || !this.meId) {
      return false;
    }
    const me = this.meId;
    const participates = (conv.participants || []).some(p => String(p.id) === me);
    return participates && String(conv.assignedUserAppId || '') !== me;
  }

  removeParticipant(userAppId: string): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !userAppId || !this.canRemoveParticipantsHere) {
      return;
    }
    this.chatService.removeParticipant({ chat: conv.chat, userAppId });
  }

  leaveConversation(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.canLeaveHere) {
      return;
    }
    const chat = conv.chat;
    Swal.fire({
      title: this.strings.leaveChat,
      text: this.strings.leaveChatConfirm,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.strings.leaveChat,
      cancelButtonText: this.strings.cancel,
      confirmButtonColor: '#f06548'
    }).then(result => {
      if (result.isConfirmed) {
        this.chatService.leaveChat(chat);
      }
    });
  }

  /** `chat_participant_removed`: saí eu (a conversa desaparece) ou saiu um colega (some da lista de participantes). */
  private onParticipantRemoved(data: any): void {
    if (!data || !data.chat || !data.userAppId) {
      return;
    }
    const chat = String(data.chat);
    const id = String(data.userAppId);
    if (id === this.meId) {
      const conv = this.activeConversation;
      if (conv && conv.chat === chat) {
        this.closeConversation();
      }
      this.toast(this.strings.leftToast);
      // Posso continuar a vê-la por supervisão: a lista decide.
      this.initChat();
      return;
    }
    const entry = this.allUsers.find(u => !!u.chat && u.chat === chat);
    if (entry) {
      this.replaceUser(entry, { participants: (entry.participants || []).filter(p => String(p.id) !== id) });
    }
    const conv = this.activeConversation;
    if (conv && conv.chat === chat) {
      this.patchActive({ participants: (conv.participants || []).filter(p => String(p.id) !== id) });
    }
  }

  /** Convidar colegas só numa conversa com um CLIENTE: as conversas entre consultores são privadas, a dois. */
  get canInviteHere(): boolean {
    return this.activeIsClient && !!this.capabilities.canInvite;
  }

  /** Visitas, propostas e qualificação só numa conversa COM UM CLIENTE (nunca entre colegas ou supervisionada). */
  get canManageVisitsHere(): boolean {
    return this.activeIsClient && !!this.capabilities.canProposeVisits;
  }

  /** Há uma visita realizada (ou marcada e já passada) para avaliar. */
  get hasVisitToEvaluate(): boolean {
    return this.chatVisits.some(v => this.isVisitEvaluable(v));
  }

  /** 2026-10-27: a ficha de visita virtual gera-se logo que a visita está agendada (nunca de uma cancelada). */
  get hasVisitForForm(): boolean {
    return this.chatVisits.some(v => this.isVisitFormable(v));
  }

  private isVisitFormable(v: { status?: string }): boolean {
    return v.status !== 'CANCELED';
  }

  private isVisitEvaluable(v: { status?: string; startAt?: string }): boolean {
    if (v.status === 'DONE') {
      return true;
    }
    if (v.status === 'CANCELED') {
      return false;
    }
    const at = v.startAt ? new Date(v.startAt).getTime() : NaN;
    return !isNaN(at) && at < Date.now();
  }

  /** Transferir: MASTER / TRANSFER_CHAT (canTransfer), ou o responsável da conversa. */
  get canTransferHere(): boolean {
    const conv = this.activeConversation;
    if (!conv || !this.activeIsClient) {
      return false;
    }
    return !!this.capabilities.canTransfer || (!!conv.assignedUserAppId && String(conv.assignedUserAppId) === this.meId);
  }

  /** Etapas do negócio e próxima ação da conversa aberta (painel de detalhes). */
  private loadDealContext(conv: ChatConversation | null): void {
    this.dealStages = null;
    this.nextAction = null;
    this.chatVisits = [];
    if (!conv || !conv.chat || conv.supervised || !isClientRow(conv)) {
      return;
    }
    const chat = conv.chat;
    this.subscriptions.add(this.chatService.getChatVisits(chat).subscribe(list => {
      if (this.activeConversation && this.activeConversation.chat === chat) {
        this.chatVisits = list;
      }
    }));
    this.subscriptions.add(this.chatService.getDealStages(chat).subscribe(stages => {
      if (this.activeConversation && this.activeConversation.chat === chat) {
        this.dealStages = stages;
      }
    }));
    this.subscriptions.add(this.chatService.getNextAction(chat).subscribe(next => {
      if (this.activeConversation && this.activeConversation.chat === chat) {
        this.nextAction = next;
      }
    }));
    this.loadMatchingProperties(conv);
    this.loadScheduled(conv);
  }

  /* ---- fase 3: imóveis que encaixam e próximo passo ---- */

  /**
   * Os filtros de "Procurar imóveis" a partir das respostas de uma qualificação
   * (7.2 v8: várias tipologias, negócios e tipos). O chip "T5+" (5) vale 5 ou mais:
   * o filtro do back-end só trata 10 como "mais de 10", por isso entram 5…10.
   */
  private qualificationFilters(a: ChatQualificationAnswers): any {
    const filters: any = {};
    const lists = qualificationLists(a);
    if (lists.bedrooms.length > 0) {
      const typology = new Set<number>();
      lists.bedrooms.forEach(n => {
        if (n >= 5) {
          [5, 6, 7, 8, 9, 10].forEach(v => typology.add(v));
        } else {
          typology.add(n);
        }
      });
      filters.typology = Array.from(typology).sort((x, y) => x - y);
    }
    if (a.budgetMin || a.budgetMax) {
      filters.price = { min: a.budgetMin || undefined, max: a.budgetMax || undefined };
    }
    if (lists.businessTypeIds.length > 0) {
      filters.businessTypes = lists.businessTypeIds;
    }
    if (lists.propertyTypeGroupIds.length > 0) {
      filters.propertyTypeGroups = lists.propertyTypeGroupIds;
    }
    return filters;
  }

  /** A última qualificação respondida da conversa. */
  private lastAnsweredQualification(conv: ChatConversation): ChatMessage | undefined {
    return conv.messages.slice().reverse().find(m => m.kind === 'qualification' && !!m.params && m.params['status'] === 'answered');
  }

  /** Até 3 imóveis que encaixam na última qualificação respondida; nada sem qualificação. */
  private loadMatchingProperties(conv: ChatConversation | null): void {
    const seq = ++this.matchingSeq;
    this.matchingProperties = [];
    this.matchingFilters = null;
    if (!conv || !conv.chat || conv.supervised || !isClientRow(conv) || !this.capabilities.canSuggestProperties) {
      return;
    }
    const card = this.lastAnsweredQualification(conv);
    const answers: ChatQualificationAnswers = card && card.params ? (card.params['answers'] || {}) : {};
    const filters = this.qualificationFilters(answers);
    if (Object.keys(filters).length === 0) {
      return;
    }
    const chat = conv.chat;
    // Só imóveis disponíveis e ativos, como no seletor (2026-10-27), no âmbito
    // mais largo do utilizador e pela mesma ordem do seletor: os imóveis de
    // outros consultores também encaixam, e a pré-seleção do "Ver e enviar"
    // só apanha a primeira página (2026-10-28).
    const body = { page: 1, status: true, onlyAvailable: true, sortColumn: 'updated', sortDirection: 'desc', ...this.widestPropertyScopeBody(), ...filters };
    this.subscriptions.add(this.propertiesService.getAll(body).pipe(catchError(() => of({ properties: [] }))).subscribe((data: any) => {
      if (seq !== this.matchingSeq || !this.activeConversation || this.activeConversation.chat !== chat) {
        return;
      }
      this.matchingFilters = filters;
      this.matchingProperties = ((data && data.properties) || []).slice(0, 3).map((p: any) => this.toPropertyPick(p));
    }));
  }

  /** "Ver e enviar": o seletor com os filtros da qualificação e os que encaixam já marcados. */
  openMatchingProperties(): void {
    this.propertyPreselect = this.matchingProperties.map(p => String(p.id));
    this.onSuggestProperty(this.matchingFilters || null);
  }

  /**
   * O próximo passo da conversa, pela ordem do funil: confirmar a visita que o
   * cliente escolheu; pedir a avaliação de uma visita que já aconteceu; propor
   * depois de uma avaliação boa; enviar imóveis depois da qualificação; propor
   * horários depois de enviar imóveis; qualificar quando ainda não se sabe nada.
   * Só em conversas de cliente, e nunca o que o consultor dispensou.
   */
  get nextStep(): ChatNextStep | null {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.activeIsClient) {
      return null;
    }
    const cache = this.nextStepCache;
    if (cache.messages === conv.messages && cache.visits === this.chatVisits && cache.matching === this.matchingProperties.length) {
      return cache.step;
    }
    const step = this.computeNextStep(conv);
    this.nextStepCache = { messages: conv.messages, visits: this.chatVisits, matching: this.matchingProperties.length, step };
    return step;
  }

  private computeNextStep(conv: ChatConversation): ChatNextStep | null {
    const s = this.strings;
    const msgs = conv.messages.filter(m => !m.deleted);
    const dismissed = this.dismissedFor(conv.chat!);
    const allowed = (key: ChatNextStep['key']) => dismissed.indexOf(key) === -1;
    const canVisits = this.canManageVisitsHere;
    const has = (kind: ChatMessageKind) => msgs.some(m => m.kind === kind);
    const last = (kind: ChatMessageKind) => msgs.slice().reverse().find(m => m.kind === kind);

    // 1. O cliente escolheu / propôs um horário e a visita ainda não está marcada.
    const open = msgs.slice().reverse().find(m => m.kind === 'visit_request' && !!m.params && (m.params['status'] === 'chosen' || m.params['status'] === 'proposed'));
    if (open && canVisits && allowed('confirmVisit')) {
      const p: any = open.params || {};
      const proposed = p.status === 'proposed';
      const when = formatWhen(proposed ? p.proposedAt : p.chosen);
      return {
        key: 'confirmVisit', message: open,
        text: (proposed ? s.nextStepAcceptProposal : s.nextStepConfirmVisit).replace('{when}', when),
        action: proposed ? s.acceptProposal : (p.conflict ? s.scheduleAnyway : s.confirmVisitFromChoice)
      };
    }
    // 2. Uma visita agendada (ou já feita) sem ficha de visita gerada (2026-10-27: logo após agendar).
    const toForm = this.chatVisits.find(v => this.isVisitFormable(v) && !(v.form && v.form.requested));
    if (toForm && canVisits && allowed('requestForm')) {
      const text = this.isVisitEvaluable(toForm) ? s.nextStepEvaluate : s.nextStepVisitForm;
      return { key: 'requestForm', visit: toForm, text: text.replace('{when}', formatWhen(toForm.startAt)), action: s.visitRequestForm };
    }
    // 3. Avaliação boa e ainda sem proposta.
    const form = last('visit_form');
    const interest = form && form.params ? Number(form.params['interest']) : NaN;
    if (form && form.params && form.params['status'] === 'answered' && interest >= 4 && !has('proposal') && canVisits && allowed('proposal')) {
      return { key: 'proposal', message: form, text: s.nextStepProposal.replace('{n}', String(interest)), action: s.sendProposal };
    }
    // 4. Qualificação respondida e nenhum imóvel enviado depois dela.
    const answered = this.lastAnsweredQualification(conv);
    if (answered && this.capabilities.canSuggestProperties && allowed('sendProperties')) {
      const index = msgs.indexOf(answered);
      const sentAfter = msgs.slice(index + 1).some(m => m.kind === 'property');
      if (!sentAfter) {
        const n = this.matchingProperties.length;
        const text = n > 1 ? s.nextStepMatching.replace('{n}', String(n)) : n === 1 ? s.nextStepMatchingOne : s.nextStepAnswered;
        return { key: 'sendProperties', message: answered, text, action: n > 0 ? s.nextStepSeeAndSend : s.nextStepSendProperties };
      }
    }
    // 5. Imóveis enviados e ainda nenhuma visita pedida ou marcada.
    if (has('property') && !has('visit_request') && !has('visit') && this.chatVisits.length === 0 && canVisits && allowed('proposeSlots')) {
      return { key: 'proposeSlots', text: s.nextStepPropose, action: s.proposeSlots };
    }
    // 6. Ainda não se sabe o que o cliente procura.
    const clientMessages = msgs.filter(m => (m.kind || 'text') === 'text' && m.userSender && m.userSender.id === conv.userId).length;
    if (!has('qualification') && clientMessages >= 2 && !has('visit') && this.canQualifyHere && allowed('qualify')) {
      return { key: 'qualify', text: s.nextStepQualify, action: s.qualify };
    }
    return null;
  }

  onNextStep(step: ChatNextStep): void {
    switch (step.key) {
      case 'confirmVisit':
        if (step.message) {
          const p: any = step.message.params || {};
          this.scheduleFromRequest(step.message, p.status === 'proposed' ? p.proposedAt : p.chosen);
        }
        break;
      case 'requestForm':
        if (step.visit) {
          this.sendVisitForm(step.visit);
        }
        break;
      case 'proposal': this.startProposal(); break;
      case 'sendProperties':
        if (this.matchingProperties.length > 0) {
          this.openMatchingProperties();
        } else {
          this.onSuggestProperty(this.matchingFilters || null);
        }
        break;
      case 'proposeSlots': this.openProposeSlots(); break;
      case 'qualify': this.openQualification(); break;
    }
  }

  dismissNextStep(step: ChatNextStep): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat) {
      return;
    }
    const list = this.dismissedFor(conv.chat);
    if (list.indexOf(step.key) === -1) {
      list.push(step.key);
    }
    this.dismissedSteps[conv.chat] = list;
    try {
      window.localStorage.setItem(NEXT_STEP_DISMISS_KEY + conv.chat, JSON.stringify(list));
    } catch {
      // sem localStorage: fica só nesta sessão
    }
    this.nextStepCache = { messages: null, visits: null, matching: -1, step: null };
  }

  private dismissedFor(chat: string): string[] {
    if (!this.dismissedSteps[chat]) {
      let saved: string[] = [];
      try {
        saved = JSON.parse(window.localStorage.getItem(NEXT_STEP_DISMISS_KEY + chat) || '[]');
      } catch {
        saved = [];
      }
      this.dismissedSteps[chat] = Array.isArray(saved) ? saved : [];
    }
    return this.dismissedSteps[chat];
  }

  /* ---- qualificação ---- */

  openQualification(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.canQualifyHere) {
      return;
    }
    this.qualEditing = false;
    this.qualMessage = null;
    this.qualFields = {};
    this.qualFieldKeys.forEach(k => this.qualFields[k] = true);
    this.openModal(this.qualificationModal, 'md');
  }

  toggleQualField(key: string): void {
    this.qualFields = { ...this.qualFields, [key]: !this.qualFields[key] };
  }

  get hasQualFields(): boolean {
    return this.qualFieldKeys.some(k => this.qualFields[k]);
  }

  /** As perguntas ligadas, por ordem. */
  get qualSelectedFields(): string[] {
    return this.qualFieldKeys.filter(k => this.qualFields[k]);
  }

  /**
   * "Enviar perguntas": o cartão vai para o cliente. Com `answerMyself` (o consultor
   * está ao telefone com o cliente), as respostas abrem-se assim que o cartão ecoar.
   */
  sendQualification(answerMyself = false): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.hasQualFields) {
      return;
    }
    this.openQualAnswersOnEcho = answerMyself;
    this.chatService.sendQualification({ chat: conv.chat, fields: this.qualSelectedFields });
    this.closeModal();
    if (!answerMyself) {
      this.expectToast('qualification', this.strings.qualificationSentToast);
    }
  }

  /** Chips do modal de respostas (7.2 v8: cada chip liga e desliga; vários ao mesmo tempo). */
  setQualBedrooms(n: number): void {
    const list = this.qualAnswers.bedroomsList || [];
    const next = list.indexOf(n) > -1 ? list.filter(v => v !== n) : [...list, n].sort((x, y) => x - y);
    this.qualAnswers = { ...this.qualAnswers, bedroomsList: next };
  }

  isQualBedroomOn(n: number): boolean {
    return (this.qualAnswers.bedroomsList || []).indexOf(n) > -1;
  }

  isQualBusinessOn(id: number): boolean {
    return (this.qualAnswers.businessTypeIds || []).indexOf(Number(id)) > -1;
  }

  /** O modal edita sempre as listas (v8), venham as respostas na forma nova ou antiga. */
  private editableQualAnswers(a: ChatQualificationAnswers): ChatQualificationAnswers {
    const lists = qualificationLists(a);
    return {
      ...a,
      propertyTypeGroupIds: lists.propertyTypeGroupIds,
      businessTypeIds: lists.businessTypeIds,
      bedroomsList: lists.bedrooms,
    };
  }

  setQualBudgetMax(value: number): void {
    this.qualAnswers = { ...this.qualAnswers, budgetMax: Number(this.qualAnswers.budgetMax) === value ? undefined : value };
  }

  setQualBusinessType(id: number): void {
    const list = this.qualAnswers.businessTypeIds || [];
    const value = Number(id);
    const next = list.indexOf(value) > -1 ? list.filter(v => v !== value) : [...list, value];
    this.qualAnswers = { ...this.qualAnswers, businessTypeIds: next };
  }

  isQualBudgetOn(value: number): boolean {
    return Number(this.qualAnswers.budgetMax) === value;
  }

  budgetChipLabel(value: number): string {
    return this.strings.budgetUpTo.replace('{value}', formatPrice(value));
  }

  /** O que já se sabe do cliente, para não perguntar duas vezes: a última qualificação respondida, senão a lead. */
  private knownQualAnswers(conv: ChatConversation): ChatQualificationAnswers {
    const last = conv.messages.slice().reverse().find(m => m.kind === 'qualification' && m.params && m.params['status'] === 'answered');
    if (last && last.params && last.params['answers']) {
      return { ...last.params['answers'] };
    }
    const out: ChatQualificationAnswers = {};
    if (conv.client && conv.client.budgetMin) {
      out.budgetMin = Number(conv.client.budgetMin);
    }
    if (conv.client && conv.client.budgetMax) {
      out.budgetMax = Number(conv.client.budgetMax);
    }
    return out;
  }

  /** Abre as respostas de um cartão (responder pelo cliente, ou corrigir o que ele respondeu). */
  private openQualAnswers(conv: ChatConversation, m: ChatMessage): void {
    const params: any = m.params || {};
    this.qualEditing = true;
    this.qualMessage = m;
    this.qualOptions = params.options || { propertyTypes: [], businessTypes: [] };
    const answered = params.status === 'answered';
    this.qualAnswers = this.editableQualAnswers(answered ? { ...(params.answers || {}) } : { ...this.knownQualAnswers(conv), ...(params.answers || {}) });
    this.openModal(this.qualificationModal, 'md');
  }

  /** Título do modal de respostas: responder pelo cliente (pendente) ou editar o que respondeu. */
  get qualAnswersTitle(): string {
    const p: any = (this.qualMessage && this.qualMessage.params) || {};
    return p.status === 'answered' ? this.strings.qualEditAnswers : this.strings.qualAnswerForClient;
  }

  onCardAction(action: ChatCardAction): void {
    const conv = this.activeConversation;
    const m = action.message;
    if (!conv || !conv.chat || !m || !m.messageId || !this.canQualifyHere) {
      return;
    }
    const params: any = m.params || {};
    if (action.type === 'editQualification') {
      this.openQualAnswers(conv, m);
    } else if (action.type === 'searchProperties') {
      this.onSuggestProperty(this.qualificationFilters(params.answers || {}));
    }
  }

  saveQualification(): void {
    const conv = this.activeConversation;
    const m = this.qualMessage;
    if (!conv || !conv.chat || !m || !m.messageId) {
      return;
    }
    const a = this.qualAnswers;
    this.chatService.answerQualification({
      chat: conv.chat, messageId: m.messageId,
      // 7.2 v8: as listas; o servidor escreve também os escalares de sempre.
      answers: {
        propertyTypeGroupIds: (a.propertyTypeGroupIds || []).map(Number),
        businessTypeIds: (a.businessTypeIds || []).map(Number),
        bedroomsList: (a.bedroomsList || []).map(Number),
        budgetMin: a.budgetMin || undefined,
        budgetMax: a.budgetMax || undefined,
        notes: a.notes || undefined
      } as any
    });
    this.closeModal();
    this.expectToast('update:' + m.messageId, this.strings.qualificationSavedToast);
  }

  /* ---- proposta ---- */

  /** Garante o negócio e abre o modal de propostas do CRM; ao fechar, regista o cartão. */
  startProposal(pick?: ChatPropertyPick | null): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || this.openNewProposal) {
      return;
    }
    const property = pick || this.conversationProperty();
    if (!property) {
      // Sem imóvel na conversa: escolhe-se um no seletor e volta-se aqui com ele.
      this.changeVisitProperty('proposal');
      return;
    }
    const chat = conv.chat;
    const rawPrice = property.price !== undefined && property.price !== null && property.price !== '' ? Number(property.price) : undefined;
    this.subscriptions.add(this.chatService.ensureDeal(chat).subscribe({
      next: res => {
        this.proposalLeadId = res.leadId;
        // O modal partilhado (app-proposal-modal) le deal.property (id, price), deal.contact e deal.agent.
        this.proposalDeal = {
          id: res.dealId,
          leadId: res.leadId,
          name: conv.name,
          property: { id: property.id, reference: property.reference, title: property.title, price: rawPrice && !isNaN(rawPrice) ? rawPrice : undefined },
          agent: { name: this.meName },
          contact: conv.client ? { name: conv.name, email: conv.client.email, phoneNumber: conv.client.phone } : { name: conv.name }
        };
        this.proposalChatId = chat;
        this.openNewProposal = true;
        this.refreshConversationLead(chat);
      },
      error: err => this.errorMsg(this.apiError.resolve(err, this.strings.dealFailed).message)
    }));
  }

  /** `new-proposal-closed` (EventService, do modal partilhado): a proposta mais recente da lead vai para a conversa. */
  private onProposalModalClosed(): void {
    if (!this.openNewProposal) {
      return;
    }
    this.openNewProposal = false;
    this.proposalDeal = null;
    // A conversa é a que estava aberta quando o modal ABRIU, não a de agora.
    const chat = this.proposalChatId;
    this.proposalChatId = null;
    if (!chat) {
      return;
    }
    this.subscriptions.add(this.chatService.shareProposal(chat).subscribe({
      next: res => {
        if (res && res.messageId) {
          this.chatService.announceMessage({ messageId: res.messageId });
          this.toast(this.strings.proposalSharedToast);
        }
        if (this.activeConversation && this.activeConversation.chat === chat) {
          this.loadDealContext(this.activeConversation);
        }
      },
      error: err => {
        // 404: o modal fechou sem proposta nova, nada a registar. Outro erro diz-se.
        if (!err || err.status !== 404) {
          this.errorMsg(this.apiError.resolve(err, this.strings.proposalNotFound).message);
        }
      }
    }));
  }

  /* ---- negócio ---- */

  advanceStage(next: ChatDealStage): void {
    const conv = this.activeConversation;
    const stages = this.dealStages;
    if (!conv || !conv.chat || !stages || !stages.dealId || !next || this.stageBusy) {
      return;
    }
    const chat = conv.chat;
    this.stageBusy = true;
    this.subscriptions.add(this.chatService.changeDealStatus(stages.dealId, next.id).subscribe({
      next: () => {
        this.stageBusy = false;
        this.toast(this.strings.stageAdvancedToast);
        this.refreshConversationLead(chat);
        this.loadDealContext(this.activeConversation);
      },
      error: err => {
        this.stageBusy = false;
        this.errorMsg(this.apiError.resolve(err, this.strings.stageFailed).message);
      }
    }));
  }

  /* ---- lembretes ---- */

  openReminder(): void {
    const conv = this.activeConversation;
    if (!conv || !this.activeIsClient) {
      return;
    }
    this.reminderQuickPicks = buildReminderPicks(new Date(), this.strings);
    const first = this.reminderQuickPicks[0];
    const at = first ? first.at : nextRoundHour(24);
    this.reminderDate = toDateInput(at);
    this.reminderTime = toTimeInput(at);
    this.reminderNote = '';
    this.openModal(this.reminderModal, 'md');
  }

  applyReminderPick(p: ChatQuickPick): void {
    this.reminderDate = p.date;
    this.reminderTime = p.time;
  }

  isReminderPickOn(p: ChatQuickPick): boolean {
    return this.reminderDate === p.date && this.reminderTime === p.time;
  }

  saveReminder(): void {
    const conv = this.activeConversation;
    const when = combineDateTime(this.reminderDate, this.reminderTime);
    if (!conv || !conv.chat || !when || !this.reminderNote.trim()) {
      return;
    }
    const chat = conv.chat;
    this.subscriptions.add(this.chatService.createReminder(chat, when.toISOString(), this.reminderNote.trim()).subscribe({
      next: () => {
        this.closeModal();
        this.toast(this.strings.reminderSavedToast);
        this.loadDealContext(this.activeConversation);
      },
      error: err => this.errorMsg(this.apiError.resolve(err, this.strings.reminderFailed).message)
    }));
  }

  /* ---- transferir ---- */

  openTransfer(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.canTransferHere) {
      return;
    }
    this.transferOptions = [];
    this.openColleaguePicker([String(conv.assignedUserAppId || '')]);
    this.transferUserId = null;
    this.transferNote = '';
    this.openModal(this.transferModal, 'md');
  }

  confirmTransfer(): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !this.transferUserId || !this.transferNote.trim()) {
      return;
    }
    this.chatService.transferChat({ chat: conv.chat, userAppId: this.transferUserId, note: this.transferNote.trim() });
    this.closeModal();
    this.expectToast('system:transferred', this.strings.transferredToast);
  }

  /* ---------------------------------------------------------------------
   * envio
   * ------------------------------------------------------------------ */

  onSend(submit: ChatComposerSubmit): void {
    const conv = this.activeConversation;
    if (!conv || !submit.text || conv.supervised) {
      return;
    }
    const kind = submit.mode === 'note' ? 'note' : 'text';
    if (kind === 'note' && !conv.chat) {
      return;
    }
    // Secção 10: a resposta só vale para a mensagem que a faixa mostrava, e numa nota se citar uma nota.
    const target = submit.replyTo && this.replyingTo && this.replyingTo.messageId === submit.replyTo && conv.chat
      && (this.replyingTo.kind !== 'note' || kind === 'note') ? this.replyingTo : null;
    this.replyingTo = null;
    const pending: ChatMessage = {
      idTemp: Date.now(),
      clientMessageId: this.nextClientId(),
      kind,
      mentions: submit.mentions,
      replyTo: target ? this.quoteOf(target) : undefined,
      message: submit.text,
      time: new Date(),
      unread: true,
      chat: conv.chat,
      files: [],
      sendState: 'sending',
      userSender: { id: String(this.meId), name: String(this.userLogged.name || '') },
      userReceiver: { id: String(conv.userId), name: conv.name, profile: conv.photo }
    };
    this.patchActive({ messages: [...conv.messages, pending] });
    this.armSendTimer(pending);
    this.emitSend(pending, conv);
  }

  /**
   * Endereçamento (contrato 6.5): com `chat` a mensagem vai à CONVERSA, a
   * todos os participantes — é o que faz uma conversa assumida ou em que se
   * foi convidado funcionar (com `IdReceiver` o servidor procurava o par
   * remetente/destinatário e podia abrir outra conversa). Sem `chat` (colega
   * com quem ainda não se falou) é o `IdReceiver` que a abre.
   */
  private emitSend(pending: ChatMessage, conv: ChatConversation): void {
    const clientMessageId = pending.clientMessageId;
    if (pending.kind === 'note') {
      this.chatService.sendNote({
        chat: String(conv.chat), message: pending.message || '', clientMessageId, mentions: (pending.mentions || []).map(x => x.id),
        replyTo: pending.replyTo ? pending.replyTo.messageId : undefined
      });
      return;
    }
    if (pending.kind === 'property') {
      const propertyId = pending.property ? String(pending.property.id) : '';
      if (!propertyId) {
        return;
      }
      this.chatService.sendProperty({
        chat: conv.chat ? conv.chat : undefined,
        IdReceiver: conv.chat ? undefined : conv.userId,
        propertyId,
        message: pending.message || undefined,
        clientMessageId
      });
      return;
    }
    this.chatService.sendMessage({
      chat: conv.chat ? conv.chat : undefined,
      IdReceiver: conv.chat ? undefined : conv.userId,
      message: pending.message,
      propertyId: conv.chat ? undefined : (conv.propertyId ? conv.propertyId : undefined),
      clientMessageId,
      replyTo: conv.chat && pending.replyTo ? pending.replyTo.messageId : undefined
    });
  }

  onRetry(message: ChatMessage): void {
    const conv = this.activeConversation;
    if (!conv) {
      return;
    }
    const current = conv.messages.find(m => m === message || (!!m.clientMessageId && m.clientMessageId === message.clientMessageId));
    if (!current || current.sendState !== 'failed') {
      return;
    }
    const again: ChatMessage = { ...current, sendState: 'sending', time: new Date() };
    this.mapMessages(m => m === current ? again : m);
    this.armSendTimer(again);
    const files = current.idTemp !== undefined ? this.pendingFiles[String(current.idTemp)] : undefined;
    if (files && files.length > 0) {
      this.uploadFiles(again, files, conv);
    } else {
      this.emitSend(again, conv);
    }
  }

  /** Contrato 6.4: até 64 caracteres de `A-Z a-z 0-9 . _ : -`. */
  private nextClientId(): string {
    this.clientSeq++;
    return Date.now().toString(36) + '-' + this.clientSeq.toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  private armSendTimer(pending: ChatMessage): void {
    const id = pending.clientMessageId;
    if (!id) {
      return;
    }
    if (this.sendTimers[id]) {
      clearTimeout(this.sendTimers[id]);
    }
    this.sendTimers[id] = setTimeout(() => {
      delete this.sendTimers[id];
      if (this.chatDisconnected) {
        // Ainda sem ligação: continua à espera, o socket.io despacha ao religar.
        this.armSendTimer(pending);
        return;
      }
      this.markFailed(id);
    }, SEND_TIMEOUT_MS);
  }

  private clearSendTimer(pending: ChatMessage): void {
    const id = pending.clientMessageId;
    if (id && this.sendTimers[id]) {
      clearTimeout(this.sendTimers[id]);
      delete this.sendTimers[id];
    }
  }

  private markFailed(clientMessageId: string): void {
    if (this.sendTimers[clientMessageId]) {
      clearTimeout(this.sendTimers[clientMessageId]);
      delete this.sendTimers[clientMessageId];
    }
    this.mapMessages(m => m.clientMessageId === clientMessageId && m.sendState === 'sending' ? { ...m, sendState: 'failed' } : m);
  }

  private failOldestPending(): void {
    const conv = this.activeConversation;
    if (!conv) {
      return;
    }
    const oldest = conv.messages.find(m => m.sendState === 'sending' && !!m.clientMessageId);
    if (oldest && oldest.clientMessageId) {
      this.markFailed(oldest.clientMessageId);
    }
  }

  /* ---------------------------------------------------------------------
   * anexos (HTTP primeiro, depois anunciados no socket - contrato 5)
   * ------------------------------------------------------------------ */

  onFiles(picked: File[]): void {
    const conv = this.activeConversation;
    if (!conv || !picked || picked.length === 0 || conv.supervised) {
      return;
    }
    // Os mesmos limites do servidor (contrato 5), verificados aqui para uma
    // recusa não custar um upload: 20 ficheiros, 5 MiB cada, 25 MiB por pedido,
    // e só os tipos que o servidor guarda. Um `type` vazio (alguns seletores)
    // fica para o servidor, que decide pelo conteúdo.
    const oversized = picked.some(file => file.size > CHAT_MAX_FILE_BYTES);
    const badType = picked.some(file => !!file.type && CHAT_ACCEPTED_TYPES.indexOf(file.type) === -1);
    const totalMb = picked.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024);
    if (picked.length > CHAT_MAX_FILES) {
      this.errorMsg(this.strings.filesTooMany);
      return;
    }
    if (badType) {
      this.errorMsg(this.strings.filesBadType);
      return;
    }
    if (oversized) {
      this.errorMsg(this.strings.fileTooBig);
      return;
    }
    if (totalMb > CHAT_MAX_TOTAL_MB) {
      this.errorMsg(this.strings.filesTooBig);
      return;
    }

    const idTemp = Date.now();
    // Os `File` seguem tal como estão; o FileReader só alimenta a
    // PRÉ-VISUALIZAÇÃO local. Tudo é resolvido antes de existir o placeholder,
    // e `order` segue a seleção.
    Promise.all(picked.map(file => this.readPreview(file))).then(previews => {
      const current = this.activeConversation;
      if (!current || current.userId !== conv.userId) {
        return;
      }
      const placeholder: ChatMessage = {
        idTemp,
        clientMessageId: this.nextClientId(),
        kind: 'text',
        message: '',
        chat: current.chat,
        time: new Date(),
        unread: true,
        isShowFiles: true,
        sendState: 'sending',
        userSender: { id: String(this.meId), name: String(this.userLogged.name || '') },
        userReceiver: { id: String(current.userId), name: current.name, profile: current.photo },
        files: picked.map((file, index) => ({
          nameTemp: previews[index],
          type: file.type,
          order: index + 1,
          name: '',
          isDelete: false,
          isLoad: false,
          originalName: file.name,
          size: file.size
        }))
      };
      this.pendingFiles[String(idTemp)] = picked;
      this.patchActive({ messages: [...current.messages, placeholder] });
      this.armSendTimer(placeholder);
      this.uploadFiles(placeholder, picked, current);
    });
  }

  /** Mensagem de voz do compositor: um anexo de áudio com a duração, pelo mesmo caminho dos ficheiros. */
  onVoice(event: { file: File; duration: number }): void {
    const conv = this.activeConversation;
    if (!conv || conv.supervised || !event || !event.file || event.file.size === 0 || event.file.size > CHAT_MAX_FILE_BYTES) {
      return;
    }
    const duration = Math.max(1, Math.min(180, Math.round(event.duration)));
    const idTemp = Date.now();
    const preview = URL.createObjectURL(event.file);
    const placeholder: ChatMessage = {
      idTemp,
      clientMessageId: this.nextClientId(),
      kind: 'text',
      message: '',
      chat: conv.chat,
      time: new Date(),
      unread: true,
      isShowFiles: true,
      sendState: 'sending',
      params: { voice: { duration } },
      userSender: { id: String(this.meId), name: String(this.userLogged.name || '') },
      userReceiver: { id: String(conv.userId), name: conv.name, profile: conv.photo },
      files: [{ nameTemp: preview, type: event.file.type || 'audio/webm', order: 1, name: '', isDelete: false, isLoad: false, originalName: event.file.name, size: event.file.size }]
    };
    this.pendingFiles[String(idTemp)] = [event.file];
    this.patchActive({ messages: [...conv.messages, placeholder] });
    this.armSendTimer(placeholder);
    this.uploadFiles(placeholder, [event.file], conv, duration);
  }

  private uploadFiles(placeholder: ChatMessage, files: File[], conv: ChatConversation, voiceDuration?: number): void {
    const idTemp = placeholder.idTemp;
    this.subscriptions.add(this.chatService.sendFiles({
      IdReceiver: String(conv.userId),
      chat: conv.chat ? conv.chat : undefined,
      files,
      idTemp,
      propertyId: conv.propertyId ? conv.propertyId : undefined,
      clientMessageId: placeholder.clientMessageId,
      voiceDuration
    }).subscribe({
      // A resposta é a própria mensagem: o placeholder fica resolvido já. O
      // servidor volta a enviá-la por `receive_message_file`; receiveMessageFile()
      // absorve a repetição por messageId.
      next: (message: any) => {
        if (message && message.messageId) {
          if (!conv.chat && message.chat) {
            this.adoptNewChat(conv, message);
          }
          this.receiveMessageFile({ ...message, idTemp });
        }
      },
      error: (err: any) => {
        if (placeholder.clientMessageId) {
          this.markFailed(placeholder.clientMessageId);
        }
        this.errorMsg(this.filesError(err));
      }
    }));
  }

  /** O motivo da recusa do envio: grande demais (413), ficheiro ilegível (415) ou o texto geral. */
  private filesError(err: any): string {
    const status = err && typeof err.status === 'number' ? err.status : 0;
    if (status === 413) {
      return this.strings.fileTooBig;
    }
    if (status === 415) {
      return this.strings.filesUnreadable;
    }
    return this.strings.filesFailed;
  }

  /**
   * Um anexo foi a PRIMEIRA mensagem de uma conversa (contrato 5): o servidor
   * acabou de criar o chat e a resposta traz o id. A mesma contabilidade que
   * onSend() + receiveMessage() fazem para uma primeira mensagem de texto.
   */
  private adoptNewChat(conv: ChatConversation, message: any): void {
    const active = this.activeConversation;
    if (active && !active.chat && active.userId === conv.userId && active.propertyReference === conv.propertyReference) {
      this.patchActive({ chat: message.chat });
    }
    const candidates = this.allUsers.filter(u => u.userId === conv.userId && !u.chat);
    const entry = candidates.length > 1
      ? candidates.find(u => u.propertyReference === conv.propertyReference)
      : candidates[0];
    if (entry) {
      this.replaceUser(entry, { chat: message.chat });
    }
  }

  /** Pré-visualização local (data URL) de um ficheiro a subir. Nunca falha: '' em erro. */
  private readPreview(file: File): Promise<string> {
    return new Promise<string>((resolve) => {
      if (file.type === 'application/pdf') {
        resolve('');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve('');
      reader.onabort = () => resolve('');
      reader.readAsDataURL(file);
    });
  }

  /* ---------------------------------------------------------------------
   * apagar (as confirmações ficam no contentor: o CRM tem sweetalert2)
   * ------------------------------------------------------------------ */

  /**
   * Apagar para todos (secção 9, regra do WhatsApp): só até 2 dias e 12 horas depois
   * de enviar. A mensagem fica "Apagou esta mensagem" para quem apagou e "Esta
   * mensagem foi apagada" para os outros.
   */
  deleteMessage(message: ChatMessage): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !message.messageId) {
      return;
    }
    const chat = conv.chat;
    const messageId = message.messageId;
    // "Para todos" só o autor, dentro da janela e numa mensagem ainda não apagada; "para mim" sempre (secção 12).
    const mine = !!message.userSender && message.userSender.id === this.meId;
    const forEveryone = mine && !message.deleted && message.kind !== 'system' && withinWindow(message.time, this.capabilities.messageDeleteWindowSeconds);
    Swal.fire({
      title: this.strings.deleteTitle,
      text: forEveryone ? this.strings.deleteChoiceText : this.strings.deleteForMeText,
      icon: 'warning',
      showCancelButton: true,
      showDenyButton: forEveryone,
      confirmButtonText: forEveryone ? this.strings.deleteForEveryone : this.strings.deleteForMe,
      denyButtonText: this.strings.deleteForMe,
      cancelButtonText: this.strings.cancel,
      confirmButtonColor: '#f06548',
      denyButtonColor: '#6c757d'
    }).then(result => {
      if (result.isConfirmed && forEveryone) {
        this.chatService.deleteMessage({ messageId, chat });
      } else if (result.isConfirmed || result.isDenied) {
        this.chatService.hideMessage({ chat, messageId });
      }
    });
  }

  /* ---- secção 12: reações, apagar para mim, enviar mais tarde ---- */

  onReact(event: { message: ChatMessage; emoji: string | null }): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !event.message.messageId) {
      return;
    }
    this.chatService.reactMessage({ chat: conv.chat, messageId: event.message.messageId, emoji: event.emoji });
  }

  /** `receive_message_reaction`: o agregado novo (chega também a quem reagiu). */
  private onMessageReaction(data: { chat: string; messageId: string; reactions: ChatReactionGroup[] }): void {
    const conv = this.activeConversation;
    if (!conv || !data || !data.messageId || (conv.chat && data.chat && conv.chat !== data.chat)) {
      return;
    }
    this.mapMessages(m => m.messageId === data.messageId ? { ...m, reactions: data.reactions || [] } : m);
  }

  /** `receive_message_hidden`: só aos meus sockets; a mensagem sai do fio (os outros continuam a vê-la). */
  private onMessageHidden(data: { chat: string; messageId: string }): void {
    const conv = this.activeConversation;
    if (!conv || !data || !data.messageId || (conv.chat && data.chat && conv.chat !== data.chat)) {
      return;
    }
    const last = conv.messages.length > 0 ? conv.messages[conv.messages.length - 1] : undefined;
    const wasLast = !!last && last.messageId === data.messageId;
    const remaining = conv.messages.filter(m => m.messageId !== data.messageId);
    this.patchActive({ messages: remaining });
    if (wasLast) {
      const prev = remaining.length > 0 ? remaining[remaining.length - 1] : undefined;
      const entry = this.allUsers.find(u => u.chat === data.chat);
      if (entry && prev) {
        this.replaceUser(entry, { message: prev.message, filesNumber: prev.files ? prev.files.length : 0, lastMessageKind: prev.kind || 'text', lastMessageDeleted: !!prev.deleted });
      }
    }
  }

  /** "Enviar mais tarde" do compositor: fica no servidor; a lista chega por `scheduled_messages`. */
  onSchedule(event: ChatComposerSchedule): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat || !event.text.trim()) {
      return;
    }
    this.scheduleToastWhen = formatWhen(event.sendAt);
    this.chatService.scheduleMessage({ chat: conv.chat, message: event.text.trim(), sendAt: event.sendAt.toISOString(), kind: event.mode === 'note' ? 'note' : 'text' });
  }

  onCancelScheduled(id: string): void {
    const conv = this.activeConversation;
    if (!conv || !conv.chat) {
      return;
    }
    this.scheduleToastWhen = null;
    this.chatService.cancelScheduledMessage({ chat: conv.chat, id });
    this.toast(this.strings.scheduledCanceledToast);
  }

  private onScheduledMessages(data: { chat: string; items: ChatScheduledMessage[] }): void {
    const conv = this.activeConversation;
    if (!conv || !data || !conv.chat || conv.chat !== data.chat) {
      return;
    }
    this.scheduled = data.items || [];
    if (this.scheduleToastWhen) {
      this.toast(this.strings.scheduledToast.replace('{when}', this.scheduleToastWhen));
      this.scheduleToastWhen = null;
    }
  }

  private loadScheduled(conv: ChatConversation | null): void {
    this.scheduled = [];
    this.scheduleToastWhen = null;
    if (conv && conv.chat && this.capabilities.canScheduleMessages) {
      this.chatService.listScheduledMessages(conv.chat);
    }
  }

  deleteFile(event: { message: ChatMessage; file: ChatMessageFile }): void {
    const conv = this.activeConversation;
    const messageId = event.message.messageId;
    const fileId = event.file.id;
    if (!conv || !messageId || !fileId) {
      return;
    }
    if (!withinWindow(event.message.time, this.capabilities.messageDeleteWindowSeconds)) {
      this.errorMsg(this.strings.deleteExpired);
      return;
    }
    const chat = conv.chat ? conv.chat : undefined;
    this.confirm().then(ok => {
      if (!ok) {
        return;
      }
      this.subscriptions.add(this.chatService.deleteFiles({ messageId, fileId, chat }).subscribe({
        // Aplicado de imediato a partir da resposta; o eco do servidor é um no-op.
        next: (result: any) => this.receiveDeleteFiles({ chat, messageId, fileId, ...(result || {}) }),
        error: (err: any) => this.errorMsg(err && err.status === 403 ? this.strings.deleteExpired : this.strings.fileDeleteFailed)
      }));
    });
  }

  /* ---------------------------------------------------------------------
   * apresentação: detalhes, pesquisa na conversa, lightbox
   * (nada disto fala com o servidor)
   * ------------------------------------------------------------------ */

  toggleDetails(): void {
    this.setDetailsOpen(!this.detailsOpen);
    if (this.detailsOpen) {
      // O painel "Atribuir a" e os detalhes partilham a coluna: os detalhes ganham.
      this.assignOpen = false;
    }
  }

  setDetailsOpen(open: boolean): void {
    this.detailsOpen = open;
    if (this.widget) {
      // No balão a escolha é momentânea: não substitui a preferência da página completa.
      return;
    }
    try {
      window.localStorage.setItem(DETAILS_PREF_KEY, open ? '1' : '0');
    } catch (e) {
      /* sem storage: a preferência vive só nesta visita */
    }
  }

  toggleThreadSearch(): void {
    this.threadSearchOpen = !this.threadSearchOpen;
    if (!this.threadSearchOpen) {
      this.threadSearch = '';
      this.threadHitIndex = -1;
      this.threadFocusKey = null;
    } else {
      setTimeout(() => {
        const el = document.querySelector<HTMLInputElement>('.chat-search__input');
        if (el) {
          el.focus();
        }
      }, 0);
    }
  }

  onThreadSearch(event: Event): void {
    this.threadSearch = (event.target as HTMLInputElement).value || '';
    const hits = this.threadHits;
    // Ao escrever, o resultado atual é o mais recente (o último do fio).
    this.threadHitIndex = hits.length > 0 ? hits.length - 1 : -1;
    this.threadFocusKey = this.threadHitIndex >= 0 ? hits[this.threadHitIndex] : null;
  }

  /** Setas / Enter da pesquisa: resultado anterior (-1) ou seguinte (+1), em ciclo. */
  stepHit(delta: number): void {
    const hits = this.threadHits;
    if (hits.length === 0) {
      return;
    }
    const base = this.threadHitIndex >= 0 ? this.threadHitIndex : hits.length;
    this.threadHitIndex = (base + delta + hits.length) % hits.length;
    this.threadFocusKey = hits[this.threadHitIndex];
  }

  /** Uma imagem de uma mensagem: lightbox com todas as imagens dessa mensagem. */
  openLightbox(event: ChatImageOpen): void {
    const images = (event.message.files || []).filter(f => !f.isDelete && f.type !== 'application/pdf' && !!(f.name || f.nameTemp));
    if (images.length === 0) {
      return;
    }
    const index = Math.max(0, images.indexOf(event.file));
    this.lightbox = { files: images, index };
    this.openModal(this.lightboxModal, 'lg', 'chat-lightbox-window');
    if (this.modalRef) {
      this.modalRef.result.then(() => this.lightbox = null, () => this.lightbox = null);
    }
  }

  lightboxStep(delta: number): void {
    const lb = this.lightbox;
    if (!lb || lb.files.length < 2) {
      return;
    }
    this.lightbox = { ...lb, index: (lb.index + delta + lb.files.length) % lb.files.length };
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.lightbox) {
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.lightboxStep(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.lightboxStep(1);
    }
  }

  /* ---------------------------------------------------------------------
   * modais (ng-bootstrap) e diálogos (sweetalert2)
   * ------------------------------------------------------------------ */

  private openModal(tpl: TemplateRef<any>, size: 'md' | 'lg' | 'xl', windowClass?: string): void {
    this.closeModal();
    const ref = this.modalService.open(tpl, { centered: true, size, windowClass });
    this.modalRef = ref;
    // A promessa do modal ANTERIOR resolve-se depois de este já estar guardado:
    // só se limpa a referência se ainda for a deste modal (senão o novo ficava sem
    // referência e closeModal() não o fechava).
    const clear = () => {
      if (this.modalRef === ref) {
        this.modalRef = null;
      }
    };
    ref.result.then(clear, clear);
  }

  private closeModal(): void {
    if (this.modalRef) {
      const ref = this.modalRef;
      this.modalRef = null;
      ref.close();
    }
  }

  private confirm(): Promise<boolean> {
    return Swal.fire({
      title: this.strings.confirmTitle,
      text: this.strings.confirmText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.strings.confirmDelete,
      cancelButtonText: this.strings.cancel,
      confirmButtonColor: '#f06548'
    }).then(result => !!result.isConfirmed);
  }

  private notice(title: string, text: string): void {
    Swal.fire({ title, text, icon: 'success', confirmButtonText: this.strings.close, confirmButtonColor: '#405189' });
  }

  private errorMsg(text: string): void {
    Swal.fire({
      title: this.strings.errorTitle,
      text,
      icon: 'warning',
      confirmButtonText: this.strings.close,
      confirmButtonColor: '#405189'
    });
  }
}

/** Preferência do painel "Detalhes": a guardada, senão aberto em ecrãs de três colunas. */
function readDetailsPref(): boolean {
  try {
    const saved = window.localStorage.getItem(DETAILS_PREF_KEY);
    if (saved === '1' || saved === '0') {
      return saved === '1';
    }
  } catch (e) {
    /* sem storage */
  }
  // Aberto por defeito só quando há espaço para três colunas (ver chat.component.scss).
  return typeof window !== 'undefined' && window.innerWidth >= 1400;
}
